import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { 
  Plus,
  Minus,
  Trash,
  CreditCard,
  Money,
  Wallet,
  Receipt,
  PencilSimple,
  Check,
  X,
  CaretLeft,
  CaretRight,
  CalendarBlank,
  Package,
  Gear,
  ArrowUp,
  ArrowDown,
  Warning,
  ArrowSquareOut,
  Clock,
  Printer,
  Percent,
  CaretDown,
  Coin,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import NumPad from '../components/NumPad';
import { StockCount } from '../components/StockCount';
import { KasseCategoryTabs } from '../components/KasseCategoryTabs';
import { StockReconciliation } from '../components/StockReconciliation';
import { toast } from 'sonner';
import { isAccessory } from '../lib/accessory';
import { useCardPayment } from '../lib/cardTerminal';
import { CardTerminalButton } from '../components/CardTerminalButton';
import { TipInput, bookTip } from '../components/TipInput';
import { JuniorAboPayButton } from '../components/JuniorAboPayButton';

const AdminCashRegister = () => {
  const payByCard = useCardPayment();
  const [tipAmount, setTipAmount] = useState('');
  const { language, API, user: currentUser } = useAuth();
  const dateLocale = language === 'de' ? de : enUS;
  const L = (en, deu) => language === 'de' ? deu : en;
  // Category ranking now follows whatever order the admin has saved in
  // Settings (categoriesList comes back from the API sorted by sort_order).
  // Fuzzy-normalise on both sides so names with different casing / umlauts
  // still map correctly.
  const _catNorm = (s) => String(s || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[\s\-_.]+/g, '');
  const catRank = (name) => {
    const target = _catNorm(name);
    if (!target) return 999;
    const idx = (categoriesList || []).findIndex((c) => _catNorm(c.name) === target);
    return idx === -1 ? 999 : idx;
  };
  const catLabel = (name) => {
    const n = _catNorm(name);
    if (!n) return name;
    if ((n.startsWith('padel') && (n.includes('academy') || n.includes('academie'))) || n === 'training' || n === 'trainings') {
      return L('Training', 'Training');
    }
    return name;
  };
  const isAdmin = currentUser?.role === 'admin';
  
  const [items, setItems] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);  // null = single-day mode; set = range mode
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('register');
  const [lagerView, setLagerView] = useState('stock');
  const [cart, setCart] = useState([]);
  // Cart-wide discount percentage. Reception can toggle 50% off for
  // promotions, loyalty gestures or damaged goods before checkout — the
  // Kasse entry is written with the discounted `custom_price`, so the
  // Tagesabrechnung reflects real revenue.
  const [discountPct, setDiscountPct] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [catFilter, setCatFilter] = useState(null);
  const [rangeCatFilter, setRangeCatFilter] = useState(null);
  const [invoiceTo, setInvoiceTo] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPaymentSplit, setShowPaymentSplit] = useState(false);
  const [splitCash, setSplitCash] = useState('');
  const [splitCard, setSplitCard] = useState('');
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({ name: '', price: '', purchase_price: '', category: '', is_active: true, track_stock: false, stock_quantity: '', stock_unit: 'pcs', stock_deduction: '1', stock_pool_id: '', min_stock: '' });
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockItem, setStockItem] = useState(null);
  const [stockAmount, setStockAmount] = useState('');
  const [stockReason, setStockReason] = useState('purchase');
  const [stockFilter, setStockFilter] = useState('all'); // all | low | empty | shopping
  const [marginInsightsOpen, setMarginInsightsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAccountingDialog, setShowAccountingDialog] = useState(false);
  const [numpadFor, setNumpadFor] = useState(null); // {lineId, name, current, max}
  const [accountingResult, setAccountingResult] = useState(null);
  const [showDetailed, setShowDetailed] = useState(false);
  const [onlyInvoices, setOnlyInvoices] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({}); // groupKey -> bool
  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  // Racket price prompt (Schläger category needs a custom sale price per transaction)
  const [racketPrompt, setRacketPrompt] = useState(null); // { item, price }
  const [variantPicker, setVariantPicker] = useState(null); // master item to pick a size from
  // Categories management
  const [categoriesList, setCategoriesList] = useState([]); // [{id, name}]
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  // Range summary (multi-day view)
  const [rangePreset, setRangePreset] = useState('today'); // 'today' | 'custom'
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [rangeSummary, setRangeSummary] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  // Pool merge dialog (e.g. share one keg between kl. Bier / gr. Bier / Herrgöttli)
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeForm, setMergeForm] = useState({
    master_name: 'Bier-Fass (Lager)',
    master_category: 'Alkoholische Getränke',
    stock_unit: 'liters',
    total_quantity: '',
    selected: {}, // item_id -> { selected: bool, deduction: string }
  });
  const [mergeSubmitting, setMergeSubmitting] = useState(false);

  // Stock-history dialog (full restock + sales history per item)
  const [historyItem, setHistoryItem] = useState(null);
  const [historyMovements, setHistoryMovements] = useState([]);
  const [historySales, setHistorySales] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const openHistoryFor = async (item) => {
    setHistoryItem(item);
    setHistoryMovements([]);
    setHistorySales([]);
    setHistoryLoading(true);
    try {
      // If this item is a pool member, history lives on the master.
      const targetItemId = item.stock_pool_id || item.id;
      const [movementsRes, salesRes] = await Promise.all([
        API.get(`/cash-register/items/${targetItemId}/stock-history`),
        API.get(`/cash-register/entries?date=2024-01-01&end_date=${new Date().toISOString().slice(0,10)}`),
      ]);
      setHistoryMovements(movementsRes.data || []);
      // Filter sales to just this item (or pool members if this is a master)
      const allSales = salesRes.data || [];
      const memberIds = new Set([item.id]);
      if (item.stock_pool_id) memberIds.add(item.stock_pool_id);
      // If this IS a master, include all pool members as well
      items.filter(i => i.stock_pool_id === targetItemId).forEach(i => memberIds.add(i.id));
      const filtered = allSales.filter(s => memberIds.has(s.item_id));
      setHistorySales(filtered);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setHistoryLoading(false); }
  };

  const loadItems = useCallback(async () => {
    try {
      // Fetch with `include_variants=true` so admin Lager screens see
      // every size row. The Kasse view filters variant children locally
      // (see `posItems` below) to render one master card per product.
      const { data } = await API.get('/cash-register/items?include_variants=true');
      // Stable alphabetical order so buttons never shift positions
      // between renders (was bugging staff: items jumped after edits).
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de', { sensitivity: 'base' }))
        : [];
      setItems(sorted);
    } catch (e) { console.error(e); }
  }, [API]);
  const loadCategories = useCallback(async () => {
    try { const { data } = await API.get('/cash-register/categories'); setCategoriesList(Array.isArray(data) ? data : []); } catch (e) { console.error(e); }
  }, [API]);

  const handleSaveCategory = async () => {
    const name = categoryName.trim();
    if (!name) return;
    try {
      if (editingCategory) {
        await API.put(`/cash-register/categories/${editingCategory.id}`, { name });
        toast.success(L('Category updated', 'Kategorie aktualisiert'));
      } else {
        await API.post('/cash-register/categories', { name });
        toast.success(L('Category created', 'Kategorie erstellt'));
      }
      setShowCategoryModal(false); setEditingCategory(null); setCategoryName('');
      loadCategories(); loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  // Sum up the current stocks of the selected items (helper for the merge dialog
  // so admin doesn't have to math 23.4 + 36.5 + 9.6 by hand).
  const mergeSelectedSum = () => {
    let sum = 0;
    items.forEach(it => {
      const sel = mergeForm.selected[it.id];
      if (sel?.selected && it.stock_quantity != null && !it.stock_pool_id) {
        sum += Number(it.stock_quantity) || 0;
      }
    });
    return Math.round(sum * 10) / 10;
  };

  const handleMergePool = async () => {
    const members = Object.entries(mergeForm.selected)
      .filter(([, v]) => v?.selected)
      .map(([item_id, v]) => ({ item_id, deduction: Number(v.deduction) || 1 }));
    if (members.length < 2) {
      toast.error(L('Pick at least 2 items to share the keg', 'Bitte mind. 2 Artikel auswählen'));
      return;
    }
    const qty = Number(mergeForm.total_quantity);
    if (!qty || qty <= 0) {
      toast.error(L('Enter total keg quantity', 'Bitte Gesamtmenge eingeben'));
      return;
    }
    if (!mergeForm.master_name.trim()) {
      toast.error(L('Enter master keg name', 'Bitte Namen für Master-Lager eingeben'));
      return;
    }
    setMergeSubmitting(true);
    try {
      const { data } = await API.post('/cash-register/items/pool/merge', {
        master_name: mergeForm.master_name.trim(),
        master_category: mergeForm.master_category,
        stock_unit: mergeForm.stock_unit,
        total_quantity: qty,
        members,
      });
      toast.success(L(
        `Pool created: ${data.linked.length} items now share ${data.total_quantity}${data.stock_unit === 'liters' ? 'L' : ''}`,
        `Pool erstellt: ${data.linked.length} Artikel teilen sich jetzt ${data.total_quantity}${data.stock_unit === 'liters' ? 'L' : ''}`
      ));
      setShowMergeModal(false);
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally {
      setMergeSubmitting(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(L(`Delete category "${cat.name}"?`, `Kategorie "${cat.name}" löschen?`))) return;
    try {
      await API.delete(`/cash-register/categories/${cat.id}`);
      toast.success(L('Deleted', 'Gelöscht'));
      loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  // Reorder categories via up/down arrows. Persists via bulk endpoint.
  const moveCategory = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= categoriesList.length) return;
    const next = [...categoriesList];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    // optimistic UI update
    setCategoriesList(next);
    try {
      await API.post('/cash-register/categories/reorder', { order: next.map(c => c.id) });
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Reorder failed', 'Sortieren fehlgeschlagen'));
      loadCategories(); // revert
    }
  };
  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const s = format(selectedDate, 'yyyy-MM-dd');
      const suffix = endDate ? `&end_date=${format(endDate, 'yyyy-MM-dd')}` : '';
      const { data } = await API.get(`/cash-register/entries?date=${s}${suffix}`);
      setEntries(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [API, selectedDate, endDate]);
  const loadSummary = useCallback(async () => {
    try {
      const s = format(selectedDate, 'yyyy-MM-dd');
      const suffix = endDate ? `&end_date=${format(endDate, 'yyyy-MM-dd')}` : '';
      const { data } = await API.get(`/cash-register/summary?date=${s}${suffix}`);
      setSummary(data);
    } catch (e) { console.error(e); }
  }, [API, selectedDate, endDate]);

  // Compute start/end dates for the current range preset
  const computedRange = React.useMemo(() => {
    const today = new Date();
    const toStr = (d) => format(d, 'yyyy-MM-dd');
    if (rangePreset === 'today') return { start: toStr(today), end: toStr(today) };
    if (rangePreset === 'custom' && rangeStart && rangeEnd) {
      return { start: toStr(rangeStart), end: toStr(rangeEnd) };
    }
    return null;
  }, [rangePreset, rangeStart, rangeEnd]);

  const loadRangeSummary = useCallback(async () => {
    if (!computedRange) { setRangeSummary(null); return; }
    try {
      setRangeLoading(true);
      const { data } = await API.get(`/cash-register/range-summary?start=${computedRange.start}&end=${computedRange.end}`);
      setRangeSummary(data);
    } catch (e) { console.error(e); toast.error('Fehler'); }
    finally { setRangeLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, computedRange]);

  useEffect(() => { loadItems(); loadCategories(); }, [loadItems, loadCategories]);
  useEffect(() => { loadEntries(); loadSummary(); }, [loadEntries, loadSummary]);
  useEffect(() => { if (activeTab === 'range') loadRangeSummary(); }, [activeTab, loadRangeSummary]);

  const isRacket = isAccessory;

  const newLineId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const addToCart = (item, variantOverride = null, customPrice = false) => {
    // Size variants: master items open a picker instead of adding directly.
    // The picker calls addToCart(masterItem, chosenVariant) so we treat the
    // variant as the underlying sale record but keep the visual grouping.
    if (item.has_variants && !variantOverride) {
      setVariantPicker({ ...item, _custom: customPrice });
      return;
    }
    const record = variantOverride
      ? {
          ...variantOverride,
          name: `${item.name} — ${variantOverride.variant_label}`,
          price: item.price,
          category: item.category,
        }
      : item;
    // Rackets: prompt for sale price before adding (staff can override retail price)
    if (isRacket(item) && (customPrice || !(record.price > 0))) {
      setRacketPrompt({ item: record, price: record.price ? record.price.toString() : '' });
      return;
    }
    const existing = cart.find(c => c.item_id === record.id && !c.is_racket);
    if (existing) { setCart(cart.map(c => c.line_id === existing.line_id ? { ...c, quantity: c.quantity + 1 } : c)); }
    else {
      setCart([...cart, {
        line_id: newLineId(),
        item_id: record.id,
        name: record.name,
        price: record.price,
        category: record.category,
        quantity: 1,
        track_stock: record.track_stock,
        stock_quantity: record.stock_quantity,
      }]);
    }
  };

  const removeFromCart = (lineId) => {
    const existing = cart.find(c => c.line_id === lineId);
    if (existing && existing.quantity > 1 && !existing.is_racket) { setCart(cart.map(c => c.line_id === lineId ? { ...c, quantity: c.quantity - 1 } : c)); }
    else { setCart(cart.filter(c => c.line_id !== lineId)); }
  };

  const incCartLine = (lineId) => {
    setCart(prev => prev.map(c => c.line_id === lineId ? { ...c, quantity: c.quantity + 1 } : c));
  };

  const setCartLineQty = (lineId, qty) => {
    const n = Math.max(0, parseInt(qty, 10) || 0);
    if (n === 0) {
      setCart(prev => prev.filter(c => c.line_id !== lineId));
    } else {
      setCart(prev => prev.map(c => c.line_id === lineId ? { ...c, quantity: n } : c));
    }
  };

  const confirmRacketPrice = () => {
    if (!racketPrompt) return;
    const priceNum = parseFloat(racketPrompt.price);
    if (!isFinite(priceNum) || priceNum <= 0) {
      toast.error(L('Please enter a valid price', 'Bitte gültigen Preis eingeben'));
      return;
    }
    const it = racketPrompt.item;
    // Each racket sale is its own cart line (don't merge — prices might differ across sales)
    setCart([...cart, {
      line_id: newLineId(),
      item_id: it.id,
      name: it.name,
      price: priceNum,
      custom_price: priceNum,
      category: it.category,
      quantity: 1,
      track_stock: it.track_stock,
      stock_quantity: it.stock_quantity,
      is_racket: true,
    }]);
    setRacketPrompt(null);
  };
  const discountFactor = () => (100 - (Number(discountPct) || 0)) / 100;
  const getCartSubtotal = () => cart.reduce((sum, item) => {
    const base = item.custom_price != null ? Number(item.custom_price) : Number(item.price);
    return sum + base * Number(item.quantity);
  }, 0);
  const getCartTotal = () => getCartSubtotal() * discountFactor();

  const openPaymentSplit = () => {
    if (cart.length === 0) return;
    const total = getCartTotal();
    // Default: full amount on selected method
    if (paymentMethod === 'cash') {
      setSplitCash(total.toFixed(2));
      setSplitCard('0.00');
    } else if (paymentMethod === 'card') {
      setSplitCash('0.00');
      setSplitCard(total.toFixed(2));
    } else {
      setSplitCash(total.toFixed(2));
      setSplitCard('0.00');
    }
    setTipAmount('');
    setShowPaymentSplit(true);
  };

  const handleSplitCashChange = (val) => {
    setSplitCash(val);
    const c = parseFloat(val) || 0;
    const remaining = Math.max(0, getCartTotal() - c);
    setSplitCard(remaining.toFixed(2));
  };

  const handleSplitCardChange = (val) => {
    setSplitCard(val);
    const c = parseFloat(val) || 0;
    const remaining = Math.max(0, getCartTotal() - c);
    setSplitCash(remaining.toFixed(2));
  };

  const confirmPayment = async () => {
    if (cart.length === 0 || checkoutLoading) return;
    setCheckoutLoading(true);
    const cashAmt = parseFloat(splitCash) || 0;
    const cardAmt = parseFloat(splitCard) || 0;
    const isSplit = cashAmt > 0 && cardAmt > 0;
    const method = isSplit ? 'split' : (cashAmt > 0 ? 'cash' : (cardAmt > 0 ? 'card' : paymentMethod));
    const f = discountFactor();
    const tip = Math.max(0, parseFloat(tipAmount) || 0);
    const term = await payByCard(cardAmt + tip, `Kasse ${new Date().toISOString().slice(11, 19)}`);
    if (!term.ok) {
      toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason}${term.uncertain ? '' : ` — ${L('nothing booked', 'nichts gebucht')}`}`, { duration: 10000 });
      setCheckoutLoading(false);
      return;
    }
    const termNote = term.info ? `Terminal #${term.info.trx_seq ?? '?'}${term.info.brand ? ` ${term.info.brand}` : ''}${term.info.auth_code ? ` Auth ${term.info.auth_code}` : ''}` : null;
    try {
      for (const item of cart) {
        // Apply cart-wide discount by lowering the unit price passed to
        // the backend. `custom_price` is per-unit — the server multiplies
        // by quantity itself, so the Kasse row shows the true amount paid.
        const basePrice = item.custom_price != null ? Number(item.custom_price) : Number(item.price);
        const effectivePrice = basePrice * f;
        const lineTotal = effectivePrice * Number(item.quantity);
        await API.post('/cash-register/entries', {
          item_id: item.item_id,
          quantity: item.quantity,
          // Send even at f=1 so the exact number the admin saw wins.
          custom_price: Number(effectivePrice.toFixed(2)),
          payment_method: method,
          cash_amount: cashAmt > 0 ? (cashAmt * lineTotal / getCartTotal()) : 0,
          card_amount: cardAmt > 0 ? (cardAmt * lineTotal / getCartTotal()) : 0,
          notes: [discountPct ? `${discountPct}% Rabatt` : null, termNote].filter(Boolean).join(' · ') || undefined,
        });
      }
      await bookTip(API, tip, termNote);
      toast.success(tip > 0 ? `${L('Sale recorded', 'Verkauf registriert')} + ${tip.toFixed(2)} CHF ${L('tip', 'Trinkgeld')}` : L('Sale recorded!', 'Verkauf registriert!'));
      setTipAmount('');
      setCart([]);
      setDiscountPct(0);
      setShowPaymentSplit(false);
      loadEntries(); loadSummary(); loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || L('Error saving', 'Fehler beim Speichern'));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const checkout = async () => {
    if (cart.length === 0 || checkoutLoading) return;
    // CSS Coins is a delayed-settlement method — no cash/card split makes
    // sense and it shouldn't touch the till. Skip the split modal and post
    // straight through with cash/card amounts at 0.
    if (paymentMethod === 'css_coins' || paymentMethod === 'invoice') {
      if (paymentMethod === 'invoice' && !invoiceTo.trim()) {
        toast.error(L('Please enter who to invoice', 'Bitte angeben, an wen die Rechnung geht'));
        return;
      }
      setCheckoutLoading(true);
      const f = discountFactor();
      const pm = paymentMethod;
      try {
        for (const item of cart) {
          const basePrice = item.custom_price != null ? Number(item.custom_price) : Number(item.price);
          const effectivePrice = basePrice * f;
          await API.post('/cash-register/entries', {
            item_id: item.item_id,
            quantity: item.quantity,
            custom_price: Number(effectivePrice.toFixed(2)),
            payment_method: pm,
            cash_amount: 0,
            card_amount: 0,
            customer_name: pm === 'invoice' ? invoiceTo.trim() : undefined,
            notes: [pm === 'invoice' ? `Rechnung an ${invoiceTo.trim()}` : null, discountPct ? `${discountPct}% Rabatt` : null].filter(Boolean).join(' · ') || undefined,
          });
        }
        toast.success(pm === 'invoice' ? L('Sale recorded (invoice)', 'Verkauf gebucht (Rechnung)') : L('Sale recorded (CSS Coins)', 'Verkauf gebucht (CSS Coins)'));
        setInvoiceTo('');
        setCart([]);
        setDiscountPct(0);
        loadEntries(); loadSummary(); loadItems();
      } catch (error) {
        toast.error(error.response?.data?.detail || L('Error saving', 'Fehler beim Speichern'));
      } finally {
        setCheckoutLoading(false);
      }
      return;
    }
    openPaymentSplit();
  };

  const deleteEntry = async (entryId) => {
    setConfirmAction({
      message: L('Delete this entry?', 'Diesen Eintrag löschen?'),
      onConfirm: async () => {
        try { await API.delete(`/cash-register/entries/${entryId}`); toast.success(L('Deleted', 'Gelöscht')); loadEntries(); loadSummary(); } catch (e) { toast.error(L('Error', 'Fehler')); }
        setConfirmAction(null);
      }
    });
  };

  const resetItemForm = () => setItemForm({ name: '', price: '', purchase_price: '', category: '', is_active: true, track_stock: false, stock_quantity: '', stock_unit: 'pcs', stock_deduction: '1', stock_pool_id: '', min_stock: '' });

  const handleSaveItem = async () => {
    try {
      const payload = {
        name: itemForm.name,
        price: parseFloat(itemForm.price),
        // Only admins can change EK. For non-admins we preserve the existing
        // value on the server (sending undefined → the field stays untouched).
        ...(isAdmin ? { purchase_price: itemForm.purchase_price === '' ? null : parseFloat(itemForm.purchase_price) } : {}),
        category: itemForm.category,
        is_active: itemForm.is_active,
        track_stock: itemForm.track_stock,
        stock_quantity: itemForm.stock_pool_id
          ? null
          : (itemForm.track_stock ? parseFloat(itemForm.stock_quantity) || 0 : null),
        stock_unit: itemForm.stock_unit,
        stock_deduction: parseFloat(itemForm.stock_deduction) || 1,
        stock_pool_id: itemForm.stock_pool_id || null,
        min_stock: itemForm.track_stock && !itemForm.stock_pool_id && itemForm.min_stock !== '' ? parseFloat(itemForm.min_stock) : null,
      };
      if (editingItem) { await API.put(`/cash-register/items/${editingItem.id}`, payload); toast.success(L('Item updated!', 'Artikel aktualisiert!')); }
      else { await API.post('/cash-register/items', payload); toast.success(L('Item created!', 'Artikel erstellt!')); }
      setShowItemModal(false); setEditingItem(null); resetItemForm(); loadItems();
    } catch (e) { toast.error(L('Error', 'Fehler')); }
  };

  const handleEditItem = (item) => { setEditingItem(item); setItemForm({ name: item.name, price: item.price.toString(), purchase_price: item.purchase_price != null ? item.purchase_price.toString() : '', category: item.category, is_active: item.is_active, track_stock: item.track_stock || false, stock_quantity: item.stock_quantity?.toString() || '', stock_unit: item.stock_unit || 'pcs', stock_deduction: (item.stock_deduction || 1).toString(), stock_pool_id: item.stock_pool_id || '', min_stock: item.min_stock != null ? item.min_stock.toString() : '' }); setShowItemModal(true); };
  const handleDeleteItem = async (itemId) => {
    setConfirmAction({
      message: L('Delete this item?', 'Diesen Artikel löschen?'),
      onConfirm: async () => {
        try { await API.delete(`/cash-register/items/${itemId}`); toast.success(L('Deleted', 'Gelöscht')); loadItems(); }
        catch (e) {
          const detail = e.response?.data?.detail;
          toast.error(detail || L('Error', 'Fehler'));
        }
        setConfirmAction(null);
      }
    });
  };
  const openStockModal = (item) => { setStockItem(item); setStockAmount(''); setStockReason('purchase'); setShowStockModal(true); };
  const handleStockUpdate = async () => {
    if (!stockItem || !stockAmount) return;
    try { await API.post(`/cash-register/items/${stockItem.id}/stock`, { quantity: parseFloat(stockAmount), reason: stockReason }); toast.success(L(`Stock updated: ${stockItem.name}`, `Lager aktualisiert: ${stockItem.name}`)); setShowStockModal(false); loadItems(); }
    catch (error) { toast.error(error.response?.data?.detail || L('Error', 'Fehler')); }
  };

  // ---------------- Size Variants ----------------
  const [newVariant, setNewVariant] = useState({ label: '', stock: '' });
  const addNewVariant = async () => {
    if (!editingItem?.id) return;
    const label = newVariant.label.trim();
    if (!label) { toast.error(L('Enter a size label', 'Grösse eingeben')); return; }
    try {
      await API.post(`/cash-register/items/${editingItem.id}/variants`, {
        variant_label: label,
        stock_quantity: parseFloat(newVariant.stock) || 0,
        stock_unit: 'pcs',
      });
      toast.success(L(`Added size ${label}`, `Grösse ${label} hinzugefügt`));
      setNewVariant({ label: '', stock: '' });
      await loadItems();
      // Refresh editingItem so the newly added variant appears in the list
      const refreshed = await API.get('/cash-register/items?include_variants=true');
      const fresh = (refreshed.data || []).find(i => i.id === editingItem.id);
      if (fresh) setEditingItem(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };
  const removeVariant = async (variantId, label) => {
    setConfirmAction({
      message: L(`Delete size ${label}?`, `Grösse ${label} löschen?`),
      onConfirm: async () => {
        try {
          await API.delete(`/cash-register/items/${variantId}`);
          toast.success(L('Deleted', 'Gelöscht'));
          await loadItems();
          const refreshed = await API.get('/cash-register/items?include_variants=true');
          const fresh = (refreshed.data || []).find(i => i.id === editingItem.id);
          if (fresh) setEditingItem(fresh);
        } catch (e) {
          toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
        }
        setConfirmAction(null);
      }
    });
  };
  const linkAsVariant = async (existingItemId, label) => {
    if (!editingItem?.id || !existingItemId) return;
    const finalLabel = (label || '').trim();
    if (!finalLabel) { toast.error(L('Enter a size label', 'Grösse eingeben')); return; }
    try {
      await API.post(`/cash-register/items/variants/merge`, {
        master_id: editingItem.id,
        variants: [{ item_id: existingItemId, variant_label: finalLabel }],
      });
      toast.success(L('Linked as size variant', 'Als Grössen-Variante verknüpft'));
      await loadItems();
      const refreshed = await API.get('/cash-register/items?include_variants=true');
      const fresh = (refreshed.data || []).find(i => i.id === editingItem.id);
      if (fresh) setEditingItem(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };
  const unlinkVariant = async (variantId) => {
    try {
      await API.post(`/cash-register/items/${variantId}/unlink-variant`);
      toast.success(L('Unlinked', 'Getrennt'));
      await loadItems();
      const refreshed = await API.get('/cash-register/items?include_variants=true');
      const fresh = (refreshed.data || []).find(i => i.id === editingItem.id);
      if (fresh) setEditingItem(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  // ------------------ Kasse (POS) items: hide variant children ------------
  // The full `items` array includes size-variant children (needed for the
  // admin Lager rows). The POS grid must show ONE card per product, so we
  // filter them out here — masters render, kids stay hidden and are only
  // reachable via the size-picker popup.
  const posItems = items.filter(i => !i.variant_of);
  const itemsByCategory = posItems.reduce((acc, item) => { if (!item.is_active) return acc; const cat = item.category; if (!acc[cat]) acc[cat] = []; acc[cat].push(item); return acc; }, {});
  const categories = Object.keys(itemsByCategory).sort((a, b) => {
    const d = catRank(a) - catRank(b);
    return d !== 0 ? d : a.localeCompare(b);
  });
  const goDate = (offset) => setSelectedDate(prev => { const d = new Date(prev); d.setDate(d.getDate() + offset); return d; });

  // Group entries by item_name + item_category for a clean Tagesabrechnung
  const rangeItemsShown = (rangeSummary?.by_item || []).filter(it => !rangeCatFilter || (it.category || L('Other', 'Sonstiges')) === rangeCatFilter);
  const viewEntries = entries.filter(e => (!onlyInvoices || e.payment_method === 'invoice') && (!catFilter || (e.item_category || L('Other', 'Sonstiges')) === catFilter));
  const groupedEntries = (() => {
    const map = new Map();
    for (const e of viewEntries) {
      const key = `${e.item_name}|||${e.item_category || ''}`;
      const cashAmt = e.cash_amount || (e.payment_method === 'cash' ? e.total_price : 0);
      const cardAmt = e.card_amount || (e.payment_method === 'card' ? e.total_price : 0);
      if (!map.has(key)) {
        map.set(key, {
          key,
          item_name: e.item_name,
          item_category: e.item_category,
          quantity: 0,
          cash_amount: 0,
          card_amount: 0,
          total_price: 0,
          unit_price: e.unit_price,
          entries: [],
          first_time: e.created_at,
          last_time: e.created_at,
        });
      }
      const g = map.get(key);
      g.quantity += Number(e.quantity) || 0;
      g.cash_amount += cashAmt;
      g.card_amount += cardAmt;
      g.total_price += e.total_price;
      g.entries.push(e);
      if (e.created_at < g.first_time) g.first_time = e.created_at;
      if (e.created_at > g.last_time) g.last_time = e.created_at;
    }
    return Array.from(map.values()).sort((a, b) => a.first_time.localeCompare(b.first_time));
  })();

  const printLagerliste = (includeFinancials = true) => {
    // Hard-lock: financial data (EK/VK/Margin) only ever leaks into the PDF
    // for admins. Non-admins always get the staff-safe variant — even if they
    // somehow trigger the function with includeFinancials=true.
    if (!isAdmin) includeFinancials = false;
    const now = new Date();
    const dateStr = format(now, 'PPPP', { locale: dateLocale });
    const timeStr = format(now, 'HH:mm');

    // Filter to currently-tracked items and respect the active filter so
    // staff can print "Einkaufsliste" directly when needed.
    const stockItems = items.filter(i => i.track_stock).filter(it => {
      if (stockFilter === 'all') return true;
      const lt = it.stock_unit === 'liters' ? 2 : 5;
      const isEmpty = (it.stock_quantity || 0) <= 0;
      const isLow = (it.stock_quantity || 0) > 0 && (it.stock_quantity || 0) <= lt;
      if (stockFilter === 'empty') return isEmpty;
      if (stockFilter === 'low') return isLow;
      if (stockFilter === 'shopping') return isEmpty || isLow;
      return true;
    });

    // Group by category, sort items alphabetically within each.
    const grouped = stockItems.reduce((acc, item) => {
      const cat = (item.category && item.category.trim()) || L('Uncategorized', 'Ohne Kategorie');
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
    const sortedCats = Object.keys(grouped).sort((a, b) => {
      const d = catRank(a) - catRank(b);
      return d !== 0 ? d : a.localeCompare(b, 'de');
    });
    sortedCats.forEach(cat => {
      grouped[cat].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
    });

    // Totals
    const totalItems = stockItems.length;
    const emptyTotal = stockItems.filter(it => (it.stock_quantity || 0) <= 0).length;
    const lowTotal = stockItems.filter(it => {
      const lt = it.stock_unit === 'liters' ? 2 : 5;
      return (it.stock_quantity || 0) > 0 && (it.stock_quantity || 0) <= lt;
    }).length;

    // Average margin across items that have BOTH EK and VK set (weighted equally).
    const withMargin = stockItems
      .map(it => {
        const vk = Number(it.price) || 0;
        const ek = it.purchase_price != null ? Number(it.purchase_price) : null;
        if (ek == null || vk <= 0) return null;
        return ((vk - ek) / vk) * 100;
      })
      .filter(m => m != null);
    const avgMargin = withMargin.length ? (withMargin.reduce((s, m) => s + m, 0) / withMargin.length) : null;

    // Per-category inventory value sums (EK × stock) — admin-only feature.
    // Skip pool members (they share the master's stock and would double-count).
    const categoryValues = {};
    let grandTotal = 0;
    let totalMissingEK = 0;
    if (includeFinancials) {
      sortedCats.forEach(cat => {
        let catSum = 0;
        grouped[cat].forEach(it => {
          if (it.stock_pool_id) return; // pool members inherit master's qty
          const qty = Number(it.stock_quantity) || 0;
          const ek = it.purchase_price != null ? Number(it.purchase_price) : null;
          if (ek != null && qty > 0) {
            catSum += ek * qty;
          } else if (qty > 0 && ek == null) {
            totalMissingEK += 1;
          }
        });
        categoryValues[cat] = catSum;
        grandTotal += catSum;
      });
    }


    const filterLabelDe = stockFilter === 'all' ? 'Alle Artikel'
      : stockFilter === 'empty' ? 'Nur leere Artikel'
      : stockFilter === 'low' ? 'Niedriger Bestand'
      : 'Einkaufsliste (leer + niedrig)';
    const filterLabelEn = stockFilter === 'all' ? 'All items'
      : stockFilter === 'empty' ? 'Empty only'
      : stockFilter === 'low' ? 'Low stock'
      : 'Shopping list (empty + low)';
    const filterLabel = language === 'de' ? filterLabelDe : filterLabelEn;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${language === 'de' ? 'Lagerliste' : 'Inventory'}${includeFinancials ? '' : ' (Empfang)'} ${format(now, 'yyyy-MM-dd HH-mm')}</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #1a1a1a; line-height: 1.5; }
  .header { border-bottom: 2px solid #1a1a1a; padding-bottom: 12px; margin-bottom: 16px; }
  .header h1 { font-size: 20px; font-weight: 700; }
  .header h2 { font-size: 13px; font-weight: 400; color: #666; margin-top: 2px; }
  .meta { font-size: 10px; color: #888; margin-top: 4px; }
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; margin-bottom: 20px; }
  .summary-box { border: 1px solid #ddd; padding: 10px; border-radius: 4px; }
  .summary-box .label { font-size: 9px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; font-weight: 600; }
  .summary-box .value { font-size: 18px; font-weight: 700; margin-top: 2px; }
  .summary-box .value.amber { color: #d97706; }
  .summary-box .value.red { color: #dc2626; }
  .category-section { margin-bottom: 14px; page-break-inside: avoid; }
  .category-title { font-weight: 700; font-size: 12px; margin-bottom: 6px; background: #f9f9f9; padding: 5px 10px; border-left: 3px solid #10b981; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f5f5f5; text-align: left; padding: 6px 8px; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; font-weight: 600; border-bottom: 1px solid #ddd; }
  td { padding: 5px 8px; border-bottom: 1px solid #eee; font-size: 11px; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .bold { font-weight: 700; }
  .row-empty { background: #fef2f2; }
  .row-low { background: #fffbeb; }
  .badge { display: inline-block; padding: 1px 6px; border-radius: 99px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
  .badge-empty { background: #fee2e2; color: #b91c1c; }
  .badge-low { background: #fef3c7; color: #92400e; }
  .sign-section { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 12px; }
  .sign-line { border-bottom: 1px solid #999; height: 36px; margin-top: 18px; }
  .sign-label { font-size: 9px; color: #888; margin-top: 4px; }
  .sign-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
<div class="header">
  <h1>Padelarena Wädenswil — ${language === 'de' ? 'Lagerliste' : 'Stock Inventory'}${includeFinancials ? '' : ` <span style="font-weight:400;font-size:14px;color:#666;">· ${language === 'de' ? 'Empfang' : 'Reception'}</span>`}</h1>
  <h2>${dateStr} · ${timeStr}</h2>
  <div class="meta">${language === 'de' ? 'Filter' : 'Filter'}: <strong>${filterLabel}</strong></div>
</div>

<div class="summary-grid" style="grid-template-columns:${includeFinancials ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr'};">
  <div class="summary-box"><div class="label">${language === 'de' ? 'Artikel total' : 'Items total'}</div><div class="value">${totalItems}</div></div>
  <div class="summary-box"><div class="label">${language === 'de' ? 'Niedriger Bestand' : 'Low stock'}</div><div class="value amber">${lowTotal}</div></div>
  <div class="summary-box"><div class="label">${language === 'de' ? 'Leer' : 'Empty'}</div><div class="value red">${emptyTotal}</div></div>
  ${includeFinancials ? `<div class="summary-box"><div class="label">${language === 'de' ? 'Ø Marge' : 'Avg margin'}</div><div class="value" style="color:#10b981;">${avgMargin != null ? avgMargin.toFixed(1) + '%' : '—'}</div></div>` : ''}
</div>

${sortedCats.length === 0
  ? `<p style="text-align:center;color:#999;padding:40px;">${language === 'de' ? 'Keine Artikel im aktuellen Filter.' : 'No items in current filter.'}</p>`
  : sortedCats.map(cat => `
<div class="category-section">
  <div class="category-title">${cat} <span style="font-weight:400;color:#888;font-size:10px;">· ${grouped[cat].length} ${language === 'de' ? 'Artikel' : 'items'}</span>${includeFinancials && categoryValues[cat] != null ? ` <span style="float:right;font-weight:700;color:#10b981;font-size:11px;">${language === 'de' ? 'Wert' : 'Value'}: ${categoryValues[cat].toFixed(2)} CHF</span>` : ''}</div>
  <table>
    <thead><tr>
      <th style="width:${includeFinancials ? '24%' : '40%'}">${language === 'de' ? 'Artikel' : 'Item'}</th>
      <th class="text-center" style="width:${includeFinancials ? '10%' : '18%'}">${language === 'de' ? 'Bestand' : 'Stock'}</th>
      ${includeFinancials ? `<th class="text-right" style="width:8%">${language === 'de' ? 'EK' : 'Cost'}</th>` : ''}
      ${includeFinancials ? `<th class="text-right" style="width:8%">${language === 'de' ? 'VK' : 'Sale'}</th>` : ''}
      ${includeFinancials ? `<th class="text-right" style="width:8%">${language === 'de' ? 'Marge' : 'Margin'}</th>` : ''}
      ${includeFinancials ? `<th class="text-right" style="width:10%">${language === 'de' ? 'Wert' : 'Value'}</th>` : ''}
      <th class="text-center" style="width:${includeFinancials ? '9%' : '14%'}">${language === 'de' ? 'Pro Verkauf' : 'Per sale'}</th>
      <th style="width:${includeFinancials ? '12%' : '14%'}">${language === 'de' ? 'Letzter Einkauf' : 'Last restock'}</th>
      <th class="text-center" style="width:${includeFinancials ? '11%' : '14%'}">${language === 'de' ? 'Physisch (Inv.)' : 'Physical count'}</th>
    </tr></thead>
    <tbody>
      ${grouped[cat].map(it => {
        const qty = it.stock_quantity || 0;
        const lt = it.stock_unit === 'liters' ? 2 : 5;
        const isEmpty = qty <= 0;
        const isLow = qty > 0 && qty <= lt;
        const unit = it.stock_unit === 'liters' ? 'L' : '';
        const qtyStr = it.stock_unit === 'liters' ? `${qty.toFixed(1)}${unit}` : `${qty}${unit}`;
        const dedStr = it.stock_deduction ? `${it.stock_deduction}${it.stock_unit === 'liters' ? 'L' : ''}` : '—';
        const lastDate = it.last_restocked_at
          ? new Date(it.last_restocked_at).toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { day:'2-digit', month:'2-digit', year:'2-digit' })
          : '—';
        const lastQty = it.last_restock_quantity
          ? ` (+${it.stock_unit === 'liters' ? Number(it.last_restock_quantity).toFixed(1) + 'L' : it.last_restock_quantity})`
          : '';
        const rowClass = isEmpty ? 'row-empty' : isLow ? 'row-low' : '';
        const badge = isEmpty
          ? `<span class="badge badge-empty">${language === 'de' ? 'Leer' : 'Empty'}</span>`
          : isLow
            ? `<span class="badge badge-low">${language === 'de' ? 'Niedrig' : 'Low'}</span>`
            : '';
        const vk = Number(it.price) || 0;
        const ek = it.purchase_price != null ? Number(it.purchase_price) : null;
        const ekStr = ek != null ? ek.toFixed(2) : '—';
        const vkStr = vk > 0 ? vk.toFixed(2) : '—';
        let marginStr = '—';
        let marginColor = '#999';
        if (ek != null && vk > 0) {
          const margin = ((vk - ek) / vk) * 100;
          marginStr = `${margin.toFixed(1)}%`;
          marginColor = margin >= 50 ? '#16a34a' : margin >= 30 ? '#d97706' : '#dc2626';
        }
        // Stock value (EK × qty). Pool members inherit the master's qty but
        // we don't recount them here — handled with `—` when stock_pool_id is set.
        let valueStr = '—';
        if (includeFinancials) {
          if (it.stock_pool_id) {
            valueStr = `<span style="color:#bbb;font-size:9px;">${language === 'de' ? '(im Pool)' : '(pooled)'}</span>`;
          } else if (ek != null && qty > 0) {
            valueStr = `${(ek * qty).toFixed(2)}`;
          } else if (qty > 0) {
            valueStr = `<span style="color:#dc2626;font-size:9px;">${language === 'de' ? 'EK fehlt' : 'no EK'}</span>`;
          }
        }
        const financialCells = includeFinancials
          ? `<td class="text-right" style="font-size:10px;color:#666;">${ekStr}</td>
             <td class="text-right" style="font-size:10px;">${vkStr}</td>
             <td class="text-right bold" style="font-size:10px;color:${marginColor};">${marginStr}</td>
             <td class="text-right" style="font-size:10px;color:#1a1a1a;font-weight:600;">${valueStr}</td>`
          : '';
        return `<tr class="${rowClass}">
          <td><span class="bold">${it.name}</span> ${badge}</td>
          <td class="text-center bold">${qtyStr}</td>
          ${financialCells}
          <td class="text-center" style="font-size:10px;">${dedStr}</td>
          <td style="font-size:10px;color:#666;">${lastDate}${lastQty}</td>
          <td class="text-center" style="border:1px dashed #aaa;border-radius:3px;color:#bbb;">_______</td>
        </tr>`;
      }).join('')}
      ${includeFinancials && categoryValues[cat] != null ? `
      <tr style="background:#f0fdf4;border-top:2px solid #10b981;">
        <td colspan="5" class="text-right bold" style="font-size:10px;color:#065f46;padding:6px 8px;">${language === 'de' ? 'Subtotal' : 'Subtotal'} <strong>${cat}</strong></td>
        <td class="text-right bold" style="font-size:11px;color:#065f46;padding:6px 8px;">${categoryValues[cat].toFixed(2)} CHF</td>
        <td colspan="3"></td>
      </tr>` : ''}
    </tbody>
  </table>
</div>`).join('')}

${includeFinancials && grandTotal > 0 ? `
<div style="margin-top:14px;padding:14px 18px;background:#10b981;color:#fff;border-radius:8px;display:flex;justify-content:space-between;align-items:center;page-break-inside:avoid;">
  <div>
    <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.5px;opacity:0.85;font-weight:600;">${language === 'de' ? 'Lagerwert total (EK)' : 'Total stock value (cost)'}</div>
    <div style="font-size:9px;opacity:0.75;margin-top:2px;">${language === 'de' ? `Summe über ${sortedCats.length} Kategorien${totalMissingEK > 0 ? ` · ${totalMissingEK} Artikel ohne EK ausgeschlossen` : ''}` : `Across ${sortedCats.length} categories${totalMissingEK > 0 ? ` · ${totalMissingEK} items without EK excluded` : ''}`}</div>
  </div>
  <div style="font-size:22px;font-weight:800;">${grandTotal.toFixed(2)} CHF</div>
</div>
` : ''}

<div class="sign-section">
  <p style="font-size:10px;color:#666;">${language === 'de'
    ? 'Trage in der Spalte „Physisch (Inv.)" den tatsächlich gezählten Bestand ein.'
    : 'Write the actual counted stock in the "Physical count" column.'}</p>
  <div class="sign-grid">
    <div><div class="sign-line"></div><div class="sign-label">${language === 'de' ? 'Gezählt durch' : 'Counted by'}</div></div>
    <div><div class="sign-line"></div><div class="sign-label">${language === 'de' ? 'Datum / Unterschrift' : 'Date / Signature'}</div></div>
  </div>
</div>

<script>window.onload = () => { setTimeout(() => window.print(), 300); };</script>
</body></html>`;

    const w = window.open('', '_blank');
    if (!w) {
      toast.error(L('Pop-up blocked — please allow pop-ups to print.', 'Pop-up blockiert — bitte Pop-ups erlauben zum Drucken.'));
      return;
    }
    w.document.write(html);
    w.document.close();
  };


  const printTagesabrechnung = () => {
    if (!summary) return;
    const dateStr = endDate
      ? `${format(selectedDate, 'PPP', { locale: dateLocale })} – ${format(endDate, 'PPP', { locale: dateLocale })}`
      : format(selectedDate, 'PPPP', { locale: dateLocale });

    // Print only cash + card entries — invoice, voucher, CSS coins and
    // online/Datatrans revenue is deliberately excluded from the paper
    // Tagesabrechnung (staff request, Feb 2026). The on-screen Tagesbericht
    // still shows everything.
    const printableEntries = (summary.entries || []).filter(
      e => e.payment_method === 'cash' || e.payment_method === 'card'
    );

    // Aggregate entries by (item_name + category) so each article shows once with total quantity
    const groupMap = new Map();
    let printTotalCash = 0;
    let printTotalCard = 0;
    for (const e of printableEntries) {
      const key = `${e.item_name}|||${e.item_category || ''}`;
      const cashAmt = e.cash_amount || (e.payment_method === 'cash' ? e.total_price : 0);
      const cardAmt = e.card_amount || (e.payment_method === 'card' ? e.total_price : 0);
      if (!groupMap.has(key)) groupMap.set(key, { item_name: e.item_name, item_category: e.item_category, quantity: 0, cash_amount: 0, card_amount: 0, total_price: 0, unit_price: e.unit_price, first_time: e.created_at, entry_count: 0 });
      const g = groupMap.get(key);
      g.quantity += Number(e.quantity) || 0;
      g.cash_amount += cashAmt;
      g.card_amount += cardAmt;
      g.total_price += (cashAmt + cardAmt);
      g.entry_count += 1;
      if (e.created_at < g.first_time) g.first_time = e.created_at;
      printTotalCash += cashAmt;
      printTotalCard += cardAmt;
    }
    const groupedPrint = Array.from(groupMap.values()).sort((a, b) => a.first_time.localeCompare(b.first_time));
    const printTotalRevenue = printTotalCash + printTotalCard;
    const printTotalEntries = printableEntries.length;
    const PM_LABEL = { cash: 'Bar', card: 'Karte', online: 'Online', invoice: 'Rechnung', twint: 'TWINT', voucher: 'Gutschein', css_coins: 'CSS Coins', credit: 'Guthaben', split: 'Split' };
    const tms = (iso) => { try { return new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
    const dms = (iso) => { try { return new Date(iso).toLocaleDateString('de-CH'); } catch { return ''; } };
    const refunds = [...(summary.refunds || [])].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    const refundsTotal = refunds.reduce((sum, e) => sum + Number(e.total_price || 0), 0);
    const deleted = summary.deleted_entries || [];
    const refundsHtml = `
<div class="category-title" style="border-left-color:#dc2626;">Rückerstattungen &amp; Stornos (${refunds.length})</div>
${refunds.length === 0 ? '<p style="font-size:10px;color:#888;margin-bottom:16px;">Keine Rückerstattungen an diesem Tag.</p>' : `<table>
  <thead><tr><th>${endDate ? 'Datum / ' : ''}Zeit</th><th>Artikel / Grund</th><th>Kunde</th><th>Zahlungsart</th><th>Erfasst von</th><th class="text-right">Betrag</th></tr></thead>
  <tbody>
    ${refunds.map(e => `<tr>
      <td>${endDate ? dms(e.created_at) + ' ' : ''}${tms(e.created_at)}</td>
      <td>${e.item_name || ''}${e.notes ? ` <span style="color:#999;font-size:9px;">(${e.notes})</span>` : ''}</td>
      <td>${e.customer_name || ''}</td>
      <td>${PM_LABEL[e.payment_method] || e.payment_method || ''}</td>
      <td>${e.created_by_name || ''}</td>
      <td class="text-right bold" style="color:#dc2626;">${Number(e.total_price || 0).toFixed(2)}</td>
    </tr>`).join('')}
    <tr class="totals-row"><td colspan="5">Total Rückerstattungen</td><td class="text-right" style="color:#dc2626;">${refundsTotal.toFixed(2)} CHF</td></tr>
  </tbody>
</table>`}
${deleted.length ? `<div class="category-title" style="border-left-color:#f59e0b;">Gelöschte Kassenbuchungen (${deleted.length})</div>
<table>
  <thead><tr><th>Gelöscht um</th><th>Artikel</th><th class="text-center">Menge</th><th>Zahlungsart</th><th>Buchungsdatum</th><th>Gelöscht von</th><th class="text-right">Betrag</th></tr></thead>
  <tbody>
    ${deleted.map(d => `<tr>
      <td>${endDate ? dms(d.deleted_at) + ' ' : ''}${tms(d.deleted_at)}</td>
      <td>${d.item_name || ''}</td>
      <td class="text-center">${d.quantity ?? ''}</td>
      <td>${PM_LABEL[d.payment_method] || d.payment_method || ''}</td>
      <td>${d.original_date ? dms(d.original_date) : ''}</td>
      <td>${d.deleted_by || ''}</td>
      <td class="text-right bold">${Number(d.total_price || 0).toFixed(2)}</td>
    </tr>`).join('')}
  </tbody>
</table>` : ''}`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Tagesabrechnung ${format(selectedDate, 'yyyy-MM-dd')}</title>
<style>
  @page { size: A4; margin: 20mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #1a1a1a; line-height: 1.5; }
  .header { border-bottom: 2px solid #1a1a1a; padding-bottom: 12px; margin-bottom: 16px; }
  .header h1 { font-size: 20px; font-weight: 700; }
  .header h2 { font-size: 13px; font-weight: 400; color: #666; margin-top: 2px; }
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; margin-bottom: 20px; }
  .summary-box { border: 1px solid #ddd; padding: 10px; border-radius: 4px; }
  .summary-box .label { font-size: 9px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; font-weight: 600; }
  .summary-box .value { font-size: 18px; font-weight: 700; margin-top: 2px; }
  .summary-box .value.green { color: #16a34a; }
  .summary-box .value.blue { color: #2563eb; }
  .summary-box .value.amber { color: #d97706; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  th { background: #f5f5f5; text-align: left; padding: 6px 8px; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; font-weight: 600; border-bottom: 1px solid #ddd; }
  td { padding: 5px 8px; border-bottom: 1px solid #eee; font-size: 10px; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .bold { font-weight: 700; }
  .footer { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 12px; }
  .footer-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 12px; }
  .sign-line { border-bottom: 1px solid #999; height: 40px; margin-top: 20px; }
  .sign-label { font-size: 9px; color: #888; margin-top: 4px; }
  .category-section { margin-bottom: 16px; }
  .category-title { font-weight: 600; font-size: 11px; margin-bottom: 6px; background: #f9f9f9; padding: 4px 8px; border-left: 3px solid #10b981; }
  .totals-row td { font-weight: 700; border-top: 2px solid #1a1a1a; border-bottom: none; padding-top: 8px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
<div class="header">
  <h1>Padelarena Wädenswil — Tagesabrechnung</h1>
  <h2>${dateStr}</h2>
</div>
<div class="summary-grid" style="grid-template-columns:repeat(5,1fr);">
  <div class="summary-box"><div class="label">Bar + Karte Total</div><div class="value">${printTotalRevenue.toFixed(2)} CHF</div></div>
  <div class="summary-box"><div class="label">Transaktionen</div><div class="value">${printTotalEntries}</div></div>
  <div class="summary-box"><div class="label">Bar in Kasse</div><div class="value green">${printTotalCash.toFixed(2)} CHF</div></div>
  <div class="summary-box"><div class="label">Kartenzahlungen</div><div class="value blue">${printTotalCard.toFixed(2)} CHF</div></div>
  <div class="summary-box"><div class="label">Rückerstattungen</div><div class="value" style="color:#dc2626;">${refundsTotal.toFixed(2)} CHF</div></div>
</div>
<p style="font-size:9px;color:#888;margin-bottom:14px;font-style:italic;">Artikelübersicht: nur Bar- und Kartenzahlungen. Rechnung, Gutschein, CSS Coins und Online werden separat abgerechnet. Rückerstattungen unten: alle Zahlungsarten (inkl. Online).</p>
<div class="category-title">Artikelübersicht (gruppiert)</div>
<table>
  <thead><tr><th>Artikel</th><th>Kategorie</th><th class="text-center">Menge</th><th class="text-right">Einzel</th><th class="text-right">Bar</th><th class="text-right">Karte</th><th class="text-right">Total</th></tr></thead>
  <tbody>
    ${groupedPrint.map(g => `<tr>
      <td>${g.item_name}${g.entry_count > 1 ? ` <span style="color:#999;font-size:9px;">(${g.entry_count}×)</span>` : ''}</td>
      <td>${g.item_category || ''}</td>
      <td class="text-center bold">${g.quantity}</td>
      <td class="text-right">${g.unit_price != null ? Number(g.unit_price).toFixed(2) : '—'}</td>
      <td class="text-right">${g.cash_amount.toFixed(2)}</td>
      <td class="text-right">${g.card_amount.toFixed(2)}</td>
      <td class="text-right bold">${g.total_price.toFixed(2)}</td>
    </tr>`).join('')}
    <tr class="totals-row">
      <td colspan="4">Total</td>
      <td class="text-right">${printTotalCash.toFixed(2)} CHF</td>
      <td class="text-right">${printTotalCard.toFixed(2)} CHF</td>
      <td class="text-right">${printTotalRevenue.toFixed(2)} CHF</td>
    </tr>
  </tbody>
</table>
${refundsHtml}
<div class="footer">
  <p style="font-size:10px;color:#666;">Kassenbericht erstellt am ${new Date().toLocaleString('de-CH')}</p>
  <div class="footer-grid">
    <div><div class="sign-line"></div><div class="sign-label">Unterschrift Mitarbeiter</div></div>
    <div><div class="sign-line"></div><div class="sign-label">Unterschrift Leitung</div></div>
  </div>
</div>
</body></html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  // Save the exact same A4 Tagesabrechnung layout as a PDF file (Feb 2026:
  // staff wanted a downloadable version that isn't a spreadsheet). Reuses
  // `printTagesabrechnung` html by re-building the HTML in a hidden iframe
  // and piping the rendered DOM through html2pdf.
  const savePdfTagesabrechnung = async () => {
    if (!summary) return;
    try {
      // Reuse printTagesabrechnung's window path: open the same window,
      // wait for it to render, then grab its body and convert to PDF.
      const w = window.open('', '_blank');
      // Small trick: printTagesabrechnung writes html to a new window. We
      // temporarily override window.open used inside it so we intercept the
      // html string. Simpler: call the same builder inline by re-running
      // printTagesabrechnung but suppressing the actual print() call.
      const origPrint = w.print;
      w.print = () => {}; // no-op; we'll trigger html2pdf instead
      // Re-run the print builder against our intercepted window.
      const originalOpen = window.open;
      window.open = () => w;
      try { printTagesabrechnung(); } finally { window.open = originalOpen; }
      // Give the browser a beat to paint, then convert.
      await new Promise(r => setTimeout(r, 700));
      const html2pdf = (await import('html2pdf.js')).default;
      const el = w.document.body;
      const dateStr = endDate
        ? `${format(selectedDate, 'yyyy-MM-dd')}_bis_${format(endDate, 'yyyy-MM-dd')}`
        : format(selectedDate, 'yyyy-MM-dd');
      await html2pdf().set({
        margin: [8, 8, 12, 8],
        filename: `Tagesabrechnung_${dateStr}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      }).from(el).save();
      w.print = origPrint;
      w.close();
      toast.success(L('PDF saved', 'PDF gespeichert'));
    } catch (e) {
      console.error(e);
      toast.error(L('PDF export failed', 'PDF-Export fehlgeschlagen'));
    }
  };

  // Period/Zeitraum PDF — accounting-ready landscape report with totals by
  // payment method, aggregated items per category and a day-by-day table.
  // Mirrors the on-screen "Zeitraum"-Tab exactly so bookkeepers can file it.
  const savePdfRangeReport = async () => {
    if (!rangeSummary || !computedRange) {
      toast.error(L('Load a period first', 'Zeitraum zuerst laden'));
      return;
    }
    try {
      // Build category-level totals (NOT individual items) — the user wants
      // a condensed overview per Kategorie, no article drill-down and no
      // day-by-day table. Mirrors the daily Tagesabrechnung look.
      //
      // Fixed buckets requested by the owner (Oct 2026). Everything from the
      // historical cash_register_entries is mapped into one of these so the
      // PDF has a stable, business-friendly layout. Unknown categories go
      // under "Sonstiges" so nothing is silently lost.
      const CANONICAL_ORDER = [
        'Padelcourt',
        'Mietschläger',
        'Nicht alkoholische Getränke',
        'Getränke alkoholisch',
        'Padelzubehör',
        'Snacks',
        'Padel Academy',
        'Golf',
        'Gutschein',
        'Rückerstattung/Storno',
      ];
      const mapToBucket = (raw) => {
        const s = (raw || '').toString().trim().toLowerCase();
        if (!s) return 'Sonstiges';
        if (s.includes('rücker') || s.includes('rucker') || s.includes('storno') || s.includes('refund')) return 'Rückerstattung/Storno';
        if (s.includes('gutschein') || s.includes('voucher')) return 'Gutschein';
        if (s.includes('golf')) return 'Golf';
        if (s.includes('mietschläger') || s.includes('mietschlager') || s.includes('miet-schläger') || s.includes('miet schläger') || (s.includes('miet') && (s.includes('schläger') || s.includes('schlager')))) return 'Mietschläger';
        // Community events (Mixin, Open Match) are padel-court bookings in disguise → fold into Padelcourt
        if (s.includes('padelcourt') || s === 'court' || s === 'courtfee' || s === 'court_fee' || s === 'community' || s.includes('mixin') || s.includes('event')) return 'Padelcourt';
        if (s.includes('alkoholfrei') || s === 'drinks' || s.includes('non-alc') || s.includes('nicht alk')) return 'Nicht alkoholische Getränke';
        if (s.includes('alkohol')) return 'Getränke alkoholisch';
        if (s === 'equipment' || s.includes('schläger') || s.includes('schlager') || s.includes('schuh') || s.includes('zubehör') || s.includes('zubehor')) return 'Padelzubehör';
        if (s === 'snacks' || s === 'snack') return 'Snacks';
        if (s.includes('academy') || s.includes('akademie') || s.includes('training')) return 'Padel Academy';
        if (s.includes('trinkgeld') || s === 'tip') return 'Trinkgeld';
        return 'Sonstiges';
      };

      const catTotals = {};
      // Pre-seed all canonical categories so they ALWAYS appear even at 0 CHF
      for (const cat of CANONICAL_ORDER) {
        catTotals[cat] = { category: cat, quantity: 0, cash_amount: 0, card_amount: 0, total_price: 0 };
      }
      // Backend `by_item` field names: category / item_name / quantity / cash / card / total
      for (const it of (rangeSummary.by_item || [])) {
        const cat = mapToBucket(it.category);
        if (!catTotals[cat]) {
          catTotals[cat] = { category: cat, quantity: 0, cash_amount: 0, card_amount: 0, total_price: 0 };
        }
        catTotals[cat].quantity += Number(it.quantity || 0);
        catTotals[cat].cash_amount += Number(it.cash || 0);
        catTotals[cat].card_amount += Number(it.card || 0);
        // Keep the "Total" column aligned with Bar+Karte so sum of column
        // equals the footer Total. CSS Coins / voucher / online entries have
        // cash=0, card=0 and therefore don't inflate this column.
        catTotals[cat].total_price += Number(it.cash || 0) + Number(it.card || 0);
      }
      // Canonical order first; append "Sonstiges" (and any other extra bucket)
      // only if it collected revenue — so the owner immediately sees when
      // uncategorised entries slip through and need re-categorisation.
      const catOrder = [
        ...CANONICAL_ORDER,
        ...Object.keys(catTotals).filter(k => !CANONICAL_ORDER.includes(k) && Math.abs(catTotals[k].total_price) > 0.01),
      ];

      const periodLabel = `${format(new Date(computedRange.start), 'PPP', { locale: dateLocale })} — ${format(new Date(computedRange.end), 'PPP', { locale: dateLocale })}`;
      const fileDateStr = `${computedRange.start}_bis_${computedRange.end}`;

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Zeitraum-Abrechnung ${fileDateStr}</title>
<style>
  @page { size: A4 landscape; margin: 14mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  /* Fixed render width = usable area of A4 landscape minus margins (297mm - 2*14mm ≈ 269mm ≈ 1017px at 96dpi).
     Giving a bit of head-room so html2canvas captures at the same proportions as the preview. */
  body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #1a1a1a; line-height: 1.5; width: 1120px; padding: 10px; background: #fff; }
  .header { border-bottom: 2px solid #1a1a1a; padding-bottom: 12px; margin-bottom: 16px; }
  .header h1 { font-size: 22px; font-weight: 700; }
  .header h2 { font-size: 13px; font-weight: 400; color: #666; margin-top: 2px; }
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 14px; margin-bottom: 20px; }
  .summary-box { border: 1px solid #ddd; padding: 12px; border-radius: 6px; }
  .summary-box .label { font-size: 9px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; font-weight: 600; }
  .summary-box .value { font-size: 20px; font-weight: 700; margin-top: 4px; }
  .summary-box .value.green { color: #16a34a; }
  .summary-box .value.blue { color: #2563eb; }
  .summary-box .value.amber { color: #d97706; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; table-layout: fixed; }
  th { background: #f1f5f9; text-align: left; padding: 12px 18px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.8px; color: #334155; font-weight: 700; border-bottom: 2px solid #cbd5e1; }
  td { padding: 12px 18px; border-bottom: 1px solid #eee; font-size: 13px; }
  /* Column widths — leaves wide money columns so numbers never wrap */
  col.col-cat   { width: 24%; }
  col.col-qty   { width: 10%; }
  col.col-cash  { width: 17%; }
  col.col-card  { width: 17%; }
  col.col-total { width: 19%; }
  col.col-pct   { width: 13%; }
  tbody tr:nth-child(odd) td { background: #fafafa; }
  tbody tr:hover td { background: #f1f5f9; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .bold { font-weight: 700; }
  .mono { font-variant-numeric: tabular-nums; }
  .totals-row td { font-weight: 800; font-size: 14px; border-top: 2px solid #1a1a1a; border-bottom: none; padding-top: 12px; padding-bottom: 12px; background: #fef3c7 !important; }
  .category-title { font-weight: 700; font-size: 13px; margin: 20px 0 8px; padding: 7px 12px; background: #ecfeff; border-left: 4px solid #0891b2; color: #0e7490; }
  .footer { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 12px; }
  .footer-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 12px; }
  .sign-line { border-bottom: 1px solid #999; height: 40px; margin-top: 20px; }
  .sign-label { font-size: 9px; color: #888; margin-top: 4px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
<!-- Inline the same stylesheet INSIDE the body too — html2canvas only
     clones the element passed to .from(), so <style> rules living in
     <head> get stripped and the exported PDF ends up unstyled. Having a
     copy inside the body guarantees the styling is captured. -->
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  /* A4 landscape usable width = 297mm - 2*14mm ≈ 269mm ≈ 1017px @96dpi.
     Keep body width just under so the right column (Anteil) never clips. */
  body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #1a1a1a; line-height: 1.4; width: 1000px; padding: 0; background: #fff; }
  .header { border-bottom: 2px solid #1a1a1a; padding-bottom: 8px; margin-bottom: 12px; }
  .header h1 { font-size: 20px; font-weight: 700; }
  .header h2 { font-size: 12px; font-weight: 400; color: #666; margin-top: 2px; }
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; margin-bottom: 12px; }
  .summary-box { border: 1px solid #ddd; padding: 10px; border-radius: 6px; }
  .summary-box .label { font-size: 9px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; font-weight: 600; }
  .summary-box .value { font-size: 18px; font-weight: 700; margin-top: 2px; }
  .summary-box .value.green { color: #16a34a; }
  .summary-box .value.blue { color: #2563eb; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 14px; table-layout: fixed; }
  th { background: #f1f5f9; text-align: left; padding: 9px 14px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.7px; color: #334155; font-weight: 700; border-bottom: 2px solid #cbd5e1; }
  td { padding: 8px 14px; border-bottom: 1px solid #eee; font-size: 12px; }
  col.col-cat   { width: 26%; }
  col.col-qty   { width: 10%; }
  col.col-cash  { width: 16%; }
  col.col-card  { width: 16%; }
  col.col-total { width: 20%; }
  col.col-pct   { width: 12%; }
  tbody tr:nth-child(odd) td { background: #fafafa; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .bold { font-weight: 700; }
  .mono { font-variant-numeric: tabular-nums; }
  .totals-row td { font-weight: 800; font-size: 13px; border-top: 2px solid #1a1a1a; border-bottom: none; padding-top: 10px; padding-bottom: 10px; background: #fef3c7 !important; }
  .category-title { font-weight: 700; font-size: 12px; margin: 12px 0 6px; padding: 6px 10px; background: #ecfeff; border-left: 4px solid #0891b2; color: #0e7490; }
  .footer { margin-top: 16px; border-top: 1px solid #ddd; padding-top: 8px; }
  .footer-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 10px; }
  .sign-line { border-bottom: 1px solid #999; height: 30px; margin-top: 14px; }
  .sign-label { font-size: 9px; color: #888; margin-top: 4px; }
</style>
<div class="header">
  <h1>Padelarena Wädenswil — Zeitraum-Abrechnung</h1>
  <h2>${periodLabel} · ${rangeSummary.days} Tage · ${rangeSummary.total_entries} Transaktionen</h2>
</div>

<div class="summary-grid" style="grid-template-columns:repeat(4,1fr);">
  <div class="summary-box"><div class="label">Bar + Karte Total</div><div class="value">${(rangeSummary.total_cash + rangeSummary.total_card).toFixed(2)} CHF</div></div>
  <div class="summary-box"><div class="label">Transaktionen</div><div class="value">${rangeSummary.total_entries}</div></div>
  <div class="summary-box"><div class="label">Bar in Kasse</div><div class="value green">${rangeSummary.total_cash.toFixed(2)} CHF</div></div>
  <div class="summary-box"><div class="label">Kartenzahlungen</div><div class="value blue">${rangeSummary.total_card.toFixed(2)} CHF</div></div>
</div>
<p style="font-size:9px;color:#888;margin-bottom:14px;font-style:italic;">Nur Bar- und Kartenzahlungen. Rechnung, Gutschein, CSS Coins und Online werden separat abgerechnet.</p>

<div class="category-title">Umsatz-Aufteilung nach Kategorie</div>
<table>
  <colgroup>
    <col class="col-cat" />
    <col class="col-qty" />
    <col class="col-cash" />
    <col class="col-card" />
    <col class="col-total" />
    <col class="col-pct" />
  </colgroup>
  <thead><tr>
    <th>Kategorie</th>
    <th class="text-center">Menge</th>
    <th class="text-right">Bar (CHF)</th>
    <th class="text-right">Karte (CHF)</th>
    <th class="text-right">Total (CHF)</th>
    <th class="text-right">Anteil</th>
  </tr></thead>
  <tbody>
    ${(() => {
      const grandTotal = catOrder.reduce((s, k) => s + (catTotals[k]?.total_price || 0), 0);
      return catOrder.map(cat => {
        const c = catTotals[cat];
        const pct = grandTotal > 0 ? (c.total_price / grandTotal * 100) : 0;
        return `<tr>
          <td class="bold">${cat}</td>
          <td class="text-center mono">${c.quantity}</td>
          <td class="text-right mono">${c.cash_amount.toFixed(2)}</td>
          <td class="text-right mono">${c.card_amount.toFixed(2)}</td>
          <td class="text-right bold mono">${c.total_price.toFixed(2)}</td>
          <td class="text-right mono">${pct.toFixed(1)}%</td>
        </tr>`;
      }).join('');
    })()}
    <tr class="totals-row">
      <td>Total</td>
      <td class="text-center mono">${catOrder.reduce((s, k) => s + catTotals[k].quantity, 0)}</td>
      <td class="text-right mono">${rangeSummary.total_cash.toFixed(2)}</td>
      <td class="text-right mono">${rangeSummary.total_card.toFixed(2)}</td>
      <td class="text-right mono">${(rangeSummary.total_cash + rangeSummary.total_card).toFixed(2)} CHF</td>
      <td class="text-right mono">100.0%</td>
    </tr>
  </tbody>
</table>

<div class="footer">
  <p style="font-size:10px;color:#666;">Zeitraum-Abrechnung erstellt am ${new Date().toLocaleString('de-CH')}</p>
  <div class="footer-grid">
    <div><div class="sign-line"></div><div class="sign-label">Unterschrift Mitarbeiter</div></div>
    <div><div class="sign-line"></div><div class="sign-label">Unterschrift Leitung</div></div>
  </div>
</div>

</body></html>`;

      // Open a NEW window with EXPLICIT dimensions — critical: without a
      // width in the features string, Chromium opens the popup at some
      // arbitrary small size and html2canvas captures the content narrow.
      const w = window.open('', '_blank', 'width=1200,height=1000,left=0,top=0');
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      // Let the popup finish layout at ~1200px window with body width 1120px
      await new Promise(r => setTimeout(r, 1200));
      try {
        const html2pdf = (await import('html2pdf.js')).default;
        await html2pdf().set({
          margin: [6, 6, 10, 6],
          filename: `Zeitraum-Abrechnung_${fileDateStr}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          // NOTE: do NOT override windowWidth/width here — those options
          // cause html2canvas to clone the document and lose our <style>
          // rules so the PDF ends up unstyled plain text. We rely on the
          // explicit `width: 1120px` on the body + a 1200px popup window
          // to get consistent layout.
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        }).from(w.document.body).save();
      } finally {
        try { w.close(); } catch(_) {}
      }
      toast.success(L('PDF saved', 'PDF gespeichert'));
    } catch (e) {
      console.error(e);
      toast.error(L('PDF export failed', 'PDF-Export fehlgeschlagen'));
    }
  };

  const exportToAccounting = async () => {
    setExportLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const { data } = await API.get(`/cash-register/accounting-export?date=${dateStr}`);
      
      if (data.positions.length === 0) {
        toast.error(L('No entries for this day', 'Keine Einträge für diesen Tag'));
        setExportLoading(false);
        return;
      }

      // Download as JSON
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `buchhaltung_${dateStr}.json`;
      a.click();
      URL.revokeObjectURL(url);

      // Also download CSV
      let csv = 'Datum;Konto;Bezeichnung;Betrag Total;Bar;Karte;Anzahl\n';
      data.positions.forEach(p => {
        csv += `${data.date};${p.account};${p.name};${p.total};${p.cash};${p.card};${p.count}\n`;
      });
      csv += `\n${data.date};TOTAL;;${data.totals.total};${data.totals.cash};${data.totals.card};${data.entry_count}\n`;
      
      const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const csvUrl = URL.createObjectURL(csvBlob);
      const csvA = document.createElement('a');
      csvA.href = csvUrl;
      csvA.download = `buchhaltung_${dateStr}.csv`;
      csvA.click();
      URL.revokeObjectURL(csvUrl);

      toast.success(L(`Accounting export downloaded (${data.positions.length} positions)`, `Buchhaltung exportiert (${data.positions.length} Positionen)`));
    } catch (e) {
      toast.error(L('Export failed', 'Export fehlgeschlagen'));
    } finally { setExportLoading(false); }
  };

  const sendToAccounting = async () => {
    setExportLoading(true);
    setAccountingResult(null);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const { data } = await API.post(`/cash-register/send-to-accounting?date=${dateStr}`);
      setAccountingResult({ ok: true, ...data });
      toast.success(L(
        `Sent to accounting (${data.entries_pushed} entries, CHF ${data.grand_total.toFixed(2)})`,
        `An Buchhaltung gesendet (${data.entries_pushed} Einträge, CHF ${data.grand_total.toFixed(2)})`
      ));
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setAccountingResult({ ok: false, error: typeof msg === 'string' ? msg : (msg?.message || 'error') });
      toast.error(L('Send to accounting failed', 'Senden an Buchhaltung fehlgeschlagen'));
    } finally { setExportLoading(false); }
  };


  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-cash-register">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">{L('Cash Register', 'Kasse')}</h1>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-slate-500">{L('Record and manage daily income & inventory', 'Tageseinnahmen erfassen und Lager verwalten')}</p>
          <CardTerminalButton />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`bg-slate-200/60 border border-slate-200 w-full grid ${isAdmin ? 'grid-cols-3 md:grid-cols-5' : 'grid-cols-3'} rounded-xl p-1 h-auto`}>
          <TabsTrigger value="register" data-testid="tab-register" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm data-[state=inactive]:text-slate-500 rounded-lg text-xs sm:text-sm font-medium py-2">
            <Receipt size={16} className="mr-1.5 hidden sm:inline" />{L('Register', 'Kasse')}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm data-[state=inactive]:text-slate-500 rounded-lg text-xs sm:text-sm font-medium py-2">
            <CalendarBlank size={16} className="mr-1.5 hidden sm:inline" />{L('Daily Report', 'Tagesbericht')}
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm data-[state=inactive]:text-slate-500 rounded-lg text-xs sm:text-sm font-medium py-2">
            <Package size={16} className="mr-1.5 hidden sm:inline" />{L('Inventory', 'Lager')}
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="range" data-testid="tab-range" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm data-[state=inactive]:text-slate-500 rounded-lg text-xs sm:text-sm font-medium py-2">
                <CalendarBlank size={16} className="mr-1.5 hidden sm:inline" />{L('Period', 'Zeitraum')}
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm data-[state=inactive]:text-slate-500 rounded-lg text-xs sm:text-sm font-medium py-2">
                <Gear size={16} className="mr-1.5 hidden sm:inline" />{L('Settings', 'Einstellungen')}
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* REGISTER TAB */}
        <TabsContent value="register" className="mt-6">
          <div className="flex justify-end mb-3">
            <JuniorAboPayButton onPaid={() => { loadEntries(); loadSummary(); }} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {categories.map(category => (
                <div key={category} className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-5">
                  <h3 className="font-semibold text-slate-700 mb-3 text-sm tracking-wide">{catLabel(category)}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {itemsByCategory[category].map(item => {
                      const hasVariants = item.has_variants && Array.isArray(item.variants) && item.variants.length > 0;
                      const availableVariants = hasVariants ? item.variants.filter(v => v.stock_quantity == null || Number(v.stock_quantity) > 0) : [];
                      const outOfStock = hasVariants
                        ? availableVariants.length === 0
                        : (item.track_stock && (item.stock_quantity || 0) <= 0);
                      const lowStock = !hasVariants && item.track_stock && item.stock_quantity > 0 && item.stock_quantity <= 5;
                      const racket = isRacket(item);
                      return (
                        <div key={item.id} className="relative">
                        <button onClick={() => !outOfStock && addToCart(item)} disabled={outOfStock}
                          className={`relative w-full h-full border p-3 rounded-xl text-left transition-all ${outOfStock ? 'bg-slate-50 border-slate-100 opacity-50 cursor-not-allowed' : racket ? 'bg-white border-blue-200 hover:border-blue-400 hover:shadow-md' : hasVariants ? 'bg-white border-purple-200 hover:border-purple-400 hover:shadow-md' : 'bg-white border-slate-200 hover:border-emerald-400 hover:shadow-md'}`}
                          data-testid={`register-item-${item.id}`}>
                          <p className="text-slate-800 font-medium text-sm truncate">{item.name}</p>
                          {racket ? (
                            <p className="text-blue-600 font-bold mt-0.5 pr-8">
                              {item.price > 0 ? `${item.price.toFixed(2)} CHF` : <span className="text-xs">{L('Enter price', 'Preis eingeben')}</span>}
                            </p>
                          ) : (
                            <p className="text-emerald-600 font-bold mt-0.5">{item.price.toFixed(2)} CHF</p>
                          )}
                          {hasVariants ? (
                            <div className="mt-1 text-xs font-medium text-purple-600 flex items-center gap-1" data-testid={`variant-summary-${item.id}`}>
                              <span>{availableVariants.length}/{item.variants.length} {L('sizes available', 'Grössen verfügbar')}</span>
                            </div>
                          ) : item.track_stock && (
                            <div className={`mt-1 text-xs font-medium ${outOfStock ? 'text-red-500' : lowStock ? 'text-amber-500' : 'text-slate-400'}`}>
                              {outOfStock ? L('Out of stock', 'Ausverkauft') : `${item.stock_quantity}${item.stock_unit === 'liters' ? 'L' : ''} ${L('in stock', 'auf Lager')}`}
                            </div>
                          )}
                          {lowStock && <Warning size={14} className="absolute top-2.5 right-2.5 text-amber-500" />}
                        </button>
                        {racket && !outOfStock && (
                          <button type="button" onClick={() => addToCart(item, null, true)}
                            className="absolute bottom-2 right-2 w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 flex items-center justify-center"
                            title={L('Individual price', 'Individueller Preis')} data-testid={`register-item-custom-price-${item.id}`}>
                            <PencilSimple size={15} weight="bold" />
                          </button>
                        )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Cart */}
            <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-5 h-fit sticky top-4">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Receipt size={20} className="text-emerald-600" /> {L('Cart', 'Warenkorb')}
              </h3>
              {cart.length === 0 ? (
                <p className="text-slate-400 text-center py-8 text-sm">{L('Cart is empty', 'Warenkorb ist leer')}</p>
              ) : (
                <>
                  <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
                    {cart.map(item => (
                      <div key={item.line_id} className="flex items-center justify-between bg-slate-50 p-2.5 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-800 text-sm font-medium truncate">
                            {item.name}
                            {item.is_racket && <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">{L('custom', 'ind.')}</span>}
                          </p>
                          <p className="text-slate-400 text-xs">{item.price.toFixed(2)} CHF</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => removeFromCart(item.line_id)} className="p-1 hover:bg-slate-200 rounded" data-testid={`cart-minus-${item.line_id}`}><Minus size={14} className="text-slate-500" /></button>
                          <button
                            type="button"
                            onClick={() => !item.is_racket && setNumpadFor({ lineId: item.line_id, name: item.name, current: item.quantity, max: item.track_stock ? Math.max(item.quantity, Math.floor(item.stock_quantity || 0)) : null })}
                            disabled={item.is_racket}
                            className={`text-slate-800 font-bold w-8 h-7 rounded text-sm border transition-colors ${item.is_racket ? 'border-transparent cursor-default' : 'border-slate-200 bg-white hover:bg-emerald-50 hover:border-emerald-300 active:scale-95'}`}
                            data-testid={`cart-qty-${item.line_id}`}
                            title={item.is_racket ? '' : L('Tap to type quantity', 'Antippen zum Eingeben')}>
                            {item.quantity}
                          </button>
                          <button
                            onClick={() => item.is_racket ? incCartLine(item.line_id) : addToCart({ id: item.item_id, name: item.name, price: item.price, category: item.category, track_stock: item.track_stock, stock_quantity: item.stock_quantity })}
                            className="p-1 hover:bg-slate-200 rounded" data-testid={`cart-plus-${item.line_id}`}><Plus size={14} className="text-slate-500" /></button>
                          <span className="text-emerald-600 font-bold w-16 text-right text-sm">{(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2 mb-4">
                    <Label className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{L('Payment Method', 'Zahlungsart')}</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[{ key: 'card', label: 'Card', icon: CreditCard }, { key: 'cash', label: 'Cash', icon: Money }, { key: 'credit', label: L('Credit', 'Guthaben'), icon: Wallet }, { key: 'css_coins', label: 'CSS Coins', icon: Coin }, { key: 'invoice', label: L('Invoice', 'Rechnung'), icon: Receipt }].map(({ key, label, icon: Icon }) => (
                        <button key={key} onClick={() => setPaymentMethod(key)}
                          className={`p-2 border rounded-lg flex flex-col items-center gap-1 transition-all ${paymentMethod === key ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`payment-${key}`}>
                          <Icon size={20} className={paymentMethod === key ? 'text-emerald-600' : 'text-slate-400'} />
                          <span className="text-xs text-slate-700 font-medium">{label}</span>
                        </button>
                      ))}
                    </div>
                    {paymentMethod === 'invoice' && (
                      <Input value={invoiceTo} onChange={(e) => setInvoiceTo(e.target.value)}
                        placeholder={L('Invoice to (name / company) *', 'Rechnung an (Name / Firma) *')}
                        className="bg-amber-50 border-amber-200 text-slate-900 rounded-lg" data-testid="invoice-to-input" />
                    )}
                    {paymentMethod === 'css_coins' && (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-1">
                        {L(
                          'ℹ️ CSS Coins: not counted in today\u2019s till. CSS transfers the total every ~2 weeks.',
                          'ℹ️ CSS Coins zählen nicht zum Tagesumsatz. CSS überweist alle ~2 Wochen die Gesamtsumme separat.'
                        )}
                      </p>
                    )}
                  </div>
                  <div className="border-t border-slate-200 pt-4">
                    {/* Cart-wide discount toggle — quick 50% off for
                        promotions, loyalty gestures, damaged goods. */}
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{L('Discount', 'Rabatt')}</span>
                      <div className="flex gap-1">
                        {[0, 50].map(pct => (
                          <button
                            key={pct}
                            type="button"
                            onClick={() => setDiscountPct(pct)}
                            className={`px-2.5 py-1 text-xs font-bold rounded-full border transition-all ${
                              discountPct === pct
                                ? 'bg-amber-500 border-amber-600 text-white shadow-sm'
                                : 'bg-white border-slate-200 text-slate-600 hover:border-amber-400 hover:text-amber-700'
                            }`}
                            data-testid={`discount-${pct}`}
                          >
                            {pct === 0 ? L('None', 'Keiner') : `${pct}%`}
                          </button>
                        ))}
                      </div>
                    </div>
                    {discountPct > 0 && (
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                        <span>{L('Subtotal', 'Zwischensumme')}</span>
                        <span className="line-through">{getCartSubtotal().toFixed(2)} CHF</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-slate-800 font-bold text-lg">Total</span>
                      <span className="text-emerald-600 font-extrabold text-2xl" data-testid="cart-total">{getCartTotal().toFixed(2)} CHF</span>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => { setCart([]); setDiscountPct(0); }} variant="outline" className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg"><X size={16} className="mr-1" /> {L('Clear', 'Leeren')}</Button>
                      <Button onClick={checkout} disabled={checkoutLoading} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold rounded-lg" data-testid="checkout-btn"><Check size={16} className="mr-1" /> {checkoutLoading ? '...' : L('Pay', 'Bezahlen')}</Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* DAILY REPORT TAB */}
        <TabsContent value="history" className="mt-6">
          <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
            <button onClick={() => goDate(-1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="date-prev"><CaretLeft size={20} className="text-slate-600" /></button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg" data-testid="date-picker"><CalendarBlank size={18} className="mr-2" />{format(selectedDate, 'PPP', { locale: dateLocale })}</Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white border-slate-200"><Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} locale={dateLocale} /></PopoverContent>
            </Popover>
            <button onClick={() => goDate(1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="date-next"><CaretRight size={20} className="text-slate-600" /></button>

            <Button onClick={() => { setSelectedDate(new Date()); setEndDate(null); }} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm" data-testid="date-today">
              {L('Today', 'Heute')}
            </Button>
            {loading && <span className="text-xs text-slate-400 ml-1">{L('Loading...', 'Laden...')}</span>}
          </div>

          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-6">
              <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="total-revenue">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('Total Revenue', 'Gesamtumsatz')}</p>
                <p className="text-3xl font-extrabold text-emerald-600">{summary.total_revenue.toFixed(2)}</p>
                <p className="text-slate-400 text-sm">CHF</p>
              </div>
              <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="total-transactions">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('Transactions', 'Transaktionen')}</p>
                <p className="text-3xl font-extrabold text-slate-800">{summary.total_entries}</p>
              </div>
              <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="total-cash-amount">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('Cash in Register', 'Bar in Kasse')}</p>
                <p className="text-2xl font-extrabold text-green-600">{(summary.total_cash || 0).toFixed(2)} CHF</p>
              </div>
              <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="total-card-amount">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('Card Payments', 'Kartenzahlungen')}</p>
                <p className="text-2xl font-extrabold text-blue-600">{(summary.total_card || 0).toFixed(2)} CHF</p>
              </div>
              <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="total-online-amount">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('Online Payments', 'Online-Zahlung')}</p>
                <p className="text-2xl font-extrabold text-indigo-600">{((summary.by_payment_method?.online) || 0).toFixed(2)} CHF</p>
              </div>
              <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="total-invoice-amount">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('On Invoice', 'Auf Rechnung')}</p>
                <p className="text-2xl font-extrabold text-amber-600">{((summary.by_payment_method?.invoice) || 0).toFixed(2)} CHF</p>
              </div>
              <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="total-css-coins">
                <p className="text-xs text-slate-400 uppercase font-semibold mb-1">CSS Coins</p>
                <p className="text-2xl font-extrabold text-fuchsia-600">{((summary.by_payment_method?.css_coins) || 0).toFixed(2)} CHF</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{L('paid ~2 weeks', 'Auszahlung ~2 Wochen')}</p>
              </div>
            </div>
          )}
          {summary && summary.total_entries > 0 && (
            <div className="mb-6 flex flex-wrap gap-3">
              <Button onClick={() => printTagesabrechnung()} className="bg-slate-800 text-white hover:bg-slate-900 font-semibold rounded-lg" data-testid="print-report-btn">
                <Receipt size={18} className="mr-2" /> {L('Print Daily Report (A4)', 'Tagesabrechnung drucken (A4)')}
              </Button>
              <Button onClick={() => savePdfTagesabrechnung()} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold rounded-lg" data-testid="save-pdf-btn">
                <ArrowSquareOut size={18} className="mr-2" /> {L('Save as PDF', 'Als PDF speichern')}
              </Button>
              <Button onClick={exportToAccounting} disabled={exportLoading} className="bg-slate-600 text-white hover:bg-slate-700 font-semibold rounded-lg gap-2" data-testid="export-accounting-btn">
                <ArrowSquareOut size={18} /> {exportLoading ? '...' : L('Download Export', 'Export herunterladen')}
              </Button>
            </div>
          )}
          {summary && Object.keys(summary.by_category || {}).length > 0 && (
            <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-5 mb-6">
              <h3 className="font-semibold text-slate-800 mb-4">{L('By Category', 'Nach Kategorie')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(summary.by_category).sort(([a], [b]) => catRank(a) - catRank(b)).map(([cat, amount]) => (
                  <div key={cat} className={`bg-slate-50 p-3 rounded-xl ${cat === 'Padelcourt' && (summary.mixin_revenue || 0) > 0 ? 'col-span-2 md:col-span-1' : ''}`} data-testid={`cat-tile-${cat}`}>
                    <p className="text-slate-500 text-sm">{catLabel(cat)}</p>
                    <p className="text-slate-800 font-bold">{amount.toFixed(2)} CHF</p>
                    {cat === 'Padelcourt' && (summary.mixin_revenue || 0) > 0 && (
                      <div className="mt-1.5 pt-1.5 border-t border-slate-200 text-xs space-y-0.5" data-testid="padelcourt-mixin-split">
                        <p className="flex justify-between text-violet-700"><span>{L('of which Mixins', 'davon Mixins')}</span><span className="font-semibold whitespace-nowrap" data-testid="padelcourt-mixin-amount">{summary.mixin_revenue.toFixed(2)} CHF</span></p>
                        <p className="flex justify-between text-slate-500"><span>{L('Court bookings', 'Platzbuchungen')}</span><span className="font-semibold whitespace-nowrap">{(amount - summary.mixin_revenue).toFixed(2)} CHF</span></p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <KasseCategoryTabs entries={entries} active={catFilter} onSelect={setCatFilter} catLabel={catLabel} L={L} />
          <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
              <h3 className="font-semibold text-slate-800">
                {showDetailed ? L('All Transactions', 'Alle Transaktionen') : L('Daily Summary', 'Tagesübersicht')}
                <span className="ml-2 text-xs text-slate-400 font-normal">
                  {(() => {
                    const shownEntries = viewEntries;
                    return showDetailed ? `${shownEntries.length} ${L('entries', 'Einträge')}` : `${groupedEntries.length} ${L('articles', 'Artikel')}`;
                  })()}
                </span>
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setOnlyInvoices(v => !v)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${onlyInvoices
                    ? 'bg-amber-500 text-white border-amber-600 hover:bg-amber-600'
                    : 'text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100'}`}
                  data-testid="only-invoices-btn"
                  title={L('Show only invoice entries', 'Nur Rechnungen anzeigen')}
                >
                  {onlyInvoices ? L('✓ Only invoices', '✓ Nur Rechnungen') : L('Only invoices', 'Nur Rechnungen')}
                </button>
                <button
                  onClick={() => setShowDetailed(v => !v)}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200"
                  data-testid="toggle-detailed-btn">
                  {showDetailed ? L('Show grouped', 'Gruppiert anzeigen') : L('Show individual', 'Einzeltransaktionen anzeigen')}
                </button>
              </div>
            </div>
            {loading ? (<div className="p-8 text-center text-slate-400">{L('Loading...', 'Laden...')}</div>
            ) : entries.length === 0 ? (<div className="p-8 text-center text-slate-400">{L('No entries for this day', 'Keine Einträge für diesen Tag')}</div>
            ) : showDetailed ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="entries-table">
                  <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Time', 'Zeit')}</th>
                    <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Item', 'Artikel')}</th>
                    <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Category', 'Kategorie')}</th>
                    <th className="text-center py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Qty', 'Menge')}</th>
                    <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Cash', 'Bar')}</th>
                    <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Card', 'Karte')}</th>
                    <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Amount', 'Betrag')}</th>
                    <th className="text-right py-3 px-4"></th>
                  </tr></thead>
                  <tbody>
                    {viewEntries.map((entry) => {
                      const cashAmt = entry.cash_amount || (entry.payment_method === 'cash' ? entry.total_price : 0);
                      const cardAmt = entry.card_amount || (entry.payment_method === 'card' ? entry.total_price : 0);
                      const isVoucher = entry.payment_method === 'voucher';
                      const isInvoice = entry.payment_method === 'invoice';
                      return (
                      <tr key={entry.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${isVoucher ? 'bg-purple-50/40' : isInvoice ? 'bg-amber-50/40' : ''}`}>
                        <td className="py-3 px-4 text-slate-500 text-sm">{format(new Date(entry.created_at), 'HH:mm')}</td>
                        <td className="py-3 px-4 text-slate-800 font-medium text-sm">
                          {entry.item_name}
                          {isVoucher && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-200 text-purple-800 font-bold">{L('VOUCHER', 'GUTSCHEIN')}</span>}
                          {isInvoice && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 font-bold">{L('INVOICE', 'RECHNUNG')}</span>}
                        </td>
                        <td className="py-3 px-4 text-slate-500 text-sm">{catLabel(entry.item_category)}</td>
                        <td className="py-3 px-4 text-center text-slate-800 text-sm">{entry.quantity}</td>
                        <td className="py-3 px-4 text-right text-sm">{cashAmt > 0 ? <span className="text-green-600 font-medium">{cashAmt.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                        <td className="py-3 px-4 text-right text-sm">{cardAmt > 0 ? <span className="text-blue-600 font-medium">{cardAmt.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                        <td className={`py-3 px-4 text-right font-bold text-sm ${isVoucher ? 'text-purple-600' : isInvoice ? 'text-amber-700' : 'text-emerald-600'}`}>{entry.total_price.toFixed(2)} CHF</td>
                        <td className="py-3 px-4 text-right"><button onClick={() => deleteEntry(entry.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" data-testid={`delete-entry-${entry.id}`}><Trash size={16} /></button></td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="grouped-entries-table">
                  <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="w-10"></th>
                    <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase font-semibold" colSpan={2}>{L('Item', 'Artikel')}</th>
                    <th className="text-center py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Qty', 'Menge')}</th>
                    <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Unit', 'Einzelpreis')}</th>
                    <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Cash', 'Bar')}</th>
                    <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Card', 'Karte')}</th>
                    <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Total', 'Gesamt')}</th>
                  </tr></thead>
                  <tbody>
                    {(() => {
                      // Group articles by category, then sort categories
                      const byCat = groupedEntries.reduce((acc, g) => {
                        const cat = g.item_category || L('Uncategorized', 'Ohne Kategorie');
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push(g);
                        return acc;
                      }, {});
                      const sortedCats = Object.keys(byCat).sort((a, b) => {
                        const d = catRank(a) - catRank(b);
                        return d !== 0 ? d : a.localeCompare(b);
                      });
                      const rows = [];
                      sortedCats.forEach(cat => {
                        const groups = byCat[cat];
                        // Compute per-category totals
                        const catQty = groups.reduce((s, g) => s + g.quantity, 0);
                        const catCash = groups.reduce((s, g) => s + (g.cash_amount || 0), 0);
                        const catCard = groups.reduce((s, g) => s + (g.card_amount || 0), 0);
                        const catTotal = groups.reduce((s, g) => s + g.total_price, 0);
                        // Category header row
                        rows.push(
                          <tr key={`cat-${cat}`} className="bg-slate-100 border-y border-slate-200" data-testid={`cat-header-${cat}`}>
                            <td colSpan={3} className="py-2 px-4 text-slate-700 text-xs font-extrabold uppercase tracking-wider">{catLabel(cat)}</td>
                            <td className="py-2 px-4 text-center text-slate-500 text-xs">{groups.length} {L('items', 'Artikel')}</td>
                            <td colSpan={4}></td>
                          </tr>
                        );
                        groups.forEach(g => {
                          const isOpen = !!expandedGroups[g.key];
                          rows.push(
                            <React.Fragment key={g.key}>
                              <tr
                                className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer"
                                onClick={() => toggleGroup(g.key)}
                                data-testid={`grouped-row-${g.item_name}`}>
                                <td className="py-3 px-2 text-center text-slate-400">
                                  <CaretRight size={14} className={`inline-block transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                                </td>
                                <td className="py-3 px-4 text-slate-800 font-medium text-sm" colSpan={2}>
                                  {g.item_name}
                                  {g.entries.length > 1 && <span className="ml-2 text-xs text-slate-400 font-normal">({g.entries.length}×)</span>}
                                </td>
                                <td className="py-3 px-4 text-center text-slate-900 text-sm font-bold">{g.quantity}</td>
                                <td className="py-3 px-4 text-right text-slate-500 text-sm">{g.unit_price != null ? `${Number(g.unit_price).toFixed(2)}` : '—'}</td>
                                <td className="py-3 px-4 text-right text-sm">{g.cash_amount > 0 ? <span className="text-green-600 font-medium">{g.cash_amount.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                                <td className="py-3 px-4 text-right text-sm">{g.card_amount > 0 ? <span className="text-blue-600 font-medium">{g.card_amount.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                                <td className="py-3 px-4 text-right text-emerald-600 font-bold text-sm">{g.total_price.toFixed(2)} CHF</td>
                              </tr>
                              {isOpen && g.entries.map(entry => {
                                const cashAmt = entry.cash_amount || (entry.payment_method === 'cash' ? entry.total_price : 0);
                                const cardAmt = entry.card_amount || (entry.payment_method === 'card' ? entry.total_price : 0);
                                return (
                                  <tr key={entry.id} className="border-b border-slate-50 bg-slate-50/30" data-testid={`grouped-sub-${entry.id}`}>
                                    <td></td>
                                    <td className="py-2 px-4 text-slate-500 text-xs" colSpan={2}>
                                      <span className="inline-block w-4 border-l border-b border-slate-300 mr-2 h-3 align-bottom" />
                                      {entry.created_at ? format(new Date(entry.created_at), 'HH:mm') : ''}
                                      <span className="text-slate-400 ml-2">· {L('by', 'von')} {entry.created_by_name || '—'}</span>
                                    </td>
                                    <td className="py-2 px-4 text-center text-slate-600 text-xs">{entry.quantity}</td>
                                    <td></td>
                                    <td className="py-2 px-4 text-right text-xs">{cashAmt > 0 ? <span className="text-green-600">{cashAmt.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                                    <td className="py-2 px-4 text-right text-xs">{cardAmt > 0 ? <span className="text-blue-600">{cardAmt.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                                    <td className="py-2 px-4 text-right text-xs">
                                      <div className="flex items-center justify-end gap-2">
                                        <span className="text-emerald-600 font-medium">{entry.total_price.toFixed(2)}</span>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); deleteEntry(entry.id); }}
                                          className="p-1 text-red-400 hover:bg-red-50 rounded"
                                          title={L('Delete this entry', 'Diesen Eintrag löschen')}
                                          data-testid={`delete-grouped-sub-${entry.id}`}>
                                          <Trash size={14} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        });
                        // Category subtotal row
                        rows.push(
                          <tr key={`cat-total-${cat}`} className="bg-emerald-50/40 border-b border-emerald-100" data-testid={`cat-subtotal-${cat}`}>
                            <td></td>
                            <td className="py-2 px-4 text-emerald-800 text-xs font-bold italic" colSpan={2}>↳ {L('Subtotal', 'Zwischensumme')} {cat}</td>
                            <td className="py-2 px-4 text-center text-emerald-800 text-xs font-bold">{catQty}</td>
                            <td></td>
                            <td className="py-2 px-4 text-right text-green-700 text-xs font-bold">{catCash > 0 ? catCash.toFixed(2) : '—'}</td>
                            <td className="py-2 px-4 text-right text-blue-700 text-xs font-bold">{catCard > 0 ? catCard.toFixed(2) : '—'}</td>
                            <td className="py-2 px-4 text-right text-emerald-800 text-xs font-extrabold">{catTotal.toFixed(2)} CHF</td>
                          </tr>
                        );
                      });
                      return rows;
                    })()}
                    {summary && (
                      <tr className="bg-slate-900 text-white font-bold">
                        <td></td>
                        <td className="py-3 px-4 text-sm" colSpan={2}>{L('TOTAL (all categories)', 'GESAMT (alle Kategorien)')}</td>
                        <td className="py-3 px-4 text-center text-sm">{groupedEntries.reduce((s, g) => s + g.quantity, 0)}</td>
                        <td></td>
                        <td className="py-3 px-4 text-right text-sm">{(summary.total_cash || 0).toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-sm">{(summary.total_card || 0).toFixed(2)}</td>
                        <td className="py-3 px-4 text-right text-emerald-300 text-sm">{summary.total_revenue.toFixed(2)} CHF</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>


        {/* PERIOD (RANGE) TAB */}
        <TabsContent value="range" className="mt-6">
          <div className="space-y-6" data-testid="range-tab-content">
            {/* Preset selector */}
            <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-4">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-semibold text-slate-600 mr-2">{L('Period', 'Zeitraum')}:</span>
                {[
                  { key: 'today',  en: 'Today',  de: 'Heute' },
                  { key: 'custom', en: 'Custom', de: 'Eigener Zeitraum' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => setRangePreset(p.key)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${rangePreset === p.key ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`range-preset-${p.key}`}
                  >
                    {L(p.en, p.de)}
                  </button>
                ))}
              </div>
              {rangePreset === 'custom' && (
                <div className="flex flex-wrap gap-3 items-center mt-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg" data-testid="range-start-picker">
                        <CalendarBlank size={16} className="mr-2" />
                        {rangeStart ? format(rangeStart, 'PP', { locale: dateLocale }) : L('From', 'Von')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white border-slate-200">
                      <Calendar mode="single" selected={rangeStart} onSelect={(d) => d && setRangeStart(d)} locale={dateLocale} />
                    </PopoverContent>
                  </Popover>
                  <span className="text-slate-400">—</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg" data-testid="range-end-picker">
                        <CalendarBlank size={16} className="mr-2" />
                        {rangeEnd ? format(rangeEnd, 'PP', { locale: dateLocale }) : L('To', 'Bis')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white border-slate-200">
                      <Calendar mode="single" selected={rangeEnd} onSelect={(d) => d && setRangeEnd(d)} locale={dateLocale} />
                    </PopoverContent>
                  </Popover>
                  <Button onClick={loadRangeSummary} disabled={!rangeStart || !rangeEnd || rangeLoading} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="range-apply-btn">
                    {rangeLoading ? '...' : L('Apply', 'Übernehmen')}
                  </Button>
                </div>
              )}
              {computedRange && (
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-slate-500">
                    {L('Viewing', 'Zeitraum')}: <span className="font-semibold text-slate-700">{computedRange.start}</span> — <span className="font-semibold text-slate-700">{computedRange.end}</span>
                  </p>
                  {rangeSummary && !rangeLoading && (
                    <Button
                      onClick={savePdfRangeReport}
                      className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold rounded-lg"
                      data-testid="range-save-pdf-btn"
                    >
                      <Printer size={16} className="mr-1.5" /> {L('Save PDF', 'PDF speichern')}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {rangeLoading && <div className="p-8 text-center text-slate-400">{L('Loading...', 'Laden...')}</div>}

            {!rangeLoading && rangeSummary && (
              <>
                {/* Totals — one card per payment method + grand total. Feb 2026
                    staff request: the range view used to only show Bar + Karte
                    and lumped everything else into the cash bucket, which
                    hid online payments, invoices, vouchers and CSS coins. */}
                {(() => {
                  const bpm = rangeSummary.by_payment_method || {};
                  const methodMeta = {
                    cash:      { label: L('Cash', 'Bar'),          color: 'text-green-600'   },
                    card:      { label: L('Card', 'Karte'),        color: 'text-blue-600'    },
                    online:    { label: L('Online', 'Online'),     color: 'text-indigo-600'  },
                    invoice:   { label: L('Invoice', 'Rechnung'),  color: 'text-amber-600'   },
                    voucher:   { label: L('Voucher', 'Gutschein'), color: 'text-purple-600'  },
                    css_coins: { label: 'CSS Coins',               color: 'text-fuchsia-600' },
                    credit:    { label: L('Credit', 'Guthaben'),   color: 'text-orange-600'  },
                  };
                  const orderedKeys = ['cash','card','online','invoice','voucher','css_coins','credit'];
                  const activeKeys = orderedKeys.filter(k => (bpm[k] || 0) !== 0);
                  // Also surface unknown methods so nothing hides.
                  for (const k of Object.keys(bpm)) if (!methodMeta[k] && bpm[k] !== 0) activeKeys.push(k);
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm md:col-span-2" data-testid="range-total-revenue">
                        <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('All payments combined', 'Alle Zahlungen zusammen')}</p>
                        <p className="text-3xl font-extrabold text-emerald-600">{rangeSummary.total_revenue.toFixed(2)} CHF</p>
                        <p className="text-slate-400 text-sm">{rangeSummary.days} {L('days', 'Tage')} · Ø {rangeSummary.avg_per_day.toFixed(2)} CHF/{L('day', 'Tag')}</p>
                      </div>
                      <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="range-total-entries">
                        <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('Transactions', 'Transaktionen')}</p>
                        <p className="text-2xl font-extrabold text-slate-800">{rangeSummary.total_entries}</p>
                      </div>
                      <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid="range-avg-per-day">
                        <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{L('Average / Day', 'Ø pro Tag')}</p>
                        <p className="text-2xl font-extrabold text-slate-800">{rangeSummary.avg_per_day.toFixed(2)} CHF</p>
                      </div>
                      {activeKeys.map(k => {
                        const meta = methodMeta[k] || { label: k, color: 'text-slate-700' };
                        return (
                          <div key={k} className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm" data-testid={`range-total-${k}`}>
                            <p className="text-xs text-slate-400 uppercase font-semibold mb-1">{meta.label}</p>
                            <p className={`text-2xl font-extrabold ${meta.color}`}>{(bpm[k] || 0).toFixed(2)} CHF</p>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Items by category — same structure as daily report */}
                {rangeSummary.by_item && rangeSummary.by_item.length > 0 && (
                  <KasseCategoryTabs
                    entries={rangeSummary.by_item.map(it => ({ item_category: it.category, total_price: it.total, quantity: it.quantity, cash_amount: it.cash, card_amount: it.card }))}
                    active={rangeCatFilter} onSelect={setRangeCatFilter} catLabel={catLabel} L={L} />
                )}
                {rangeSummary.by_item && rangeSummary.by_item.length > 0 && (
                  <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden" data-testid="range-by-item-table">
                    <div className="p-5 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-800">{L('Items grouped by category', 'Artikel nach Kategorie')}</h3>
                      <p className="text-xs text-slate-400 mt-1">{L('Aggregated over the selected period', 'Aggregiert über den gewählten Zeitraum')}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                          <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Item', 'Artikel')}</th>
                          <th className="text-center py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Qty', 'Menge')}</th>
                          <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Unit', 'Einzelpreis')}</th>
                          <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Cash', 'Bar')}</th>
                          <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Card', 'Karte')}</th>
                          <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Total', 'Gesamt')}</th>
                        </tr></thead>
                        <tbody>
                          {(() => {
                            const byCat = rangeItemsShown.reduce((acc, it) => {
                              const c = it.category || L('Uncategorized', 'Ohne Kategorie');
                              if (!acc[c]) acc[c] = [];
                              acc[c].push(it);
                              return acc;
                            }, {});
                            const sortedCats = Object.keys(byCat).sort((a, b) => a.localeCompare(b));
                            const rows = [];
                            sortedCats.forEach(cat => {
                              const list = byCat[cat].sort((a, b) => b.total - a.total);
                              const catQty = list.reduce((s, it) => s + it.quantity, 0);
                              const catCash = list.reduce((s, it) => s + it.cash, 0);
                              const catCard = list.reduce((s, it) => s + it.card, 0);
                              const catTotal = list.reduce((s, it) => s + it.total, 0);
                              rows.push(
                                <tr key={`rcat-${cat}`} className="bg-slate-100 border-y border-slate-200" data-testid={`range-cat-header-${cat}`}>
                                  <td colSpan={2} className="py-2 px-4 text-slate-700 text-xs font-extrabold uppercase tracking-wider">{cat}</td>
                                  <td className="py-2 px-4 text-center text-slate-500 text-xs">{list.length} {L('items', 'Artikel')}</td>
                                  <td colSpan={3}></td>
                                </tr>
                              );
                              list.forEach((it, idx) => {
                                rows.push(
                                  <tr key={`ri-${cat}-${idx}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                                    <td className="py-3 px-4 text-slate-800 font-medium text-sm">{it.item_name}</td>
                                    <td className="py-3 px-4 text-center text-slate-900 text-sm font-bold">{it.quantity}</td>
                                    <td className="py-3 px-4 text-right text-slate-500 text-sm">{it.unit_price != null ? Number(it.unit_price).toFixed(2) : '—'}</td>
                                    <td className="py-3 px-4 text-right text-sm">{it.cash > 0 ? <span className="text-green-600 font-medium">{it.cash.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                                    <td className="py-3 px-4 text-right text-sm">{it.card > 0 ? <span className="text-blue-600 font-medium">{it.card.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                                    <td className="py-3 px-4 text-right text-emerald-600 font-bold text-sm">{it.total.toFixed(2)} CHF</td>
                                  </tr>
                                );
                              });
                              rows.push(
                                <tr key={`rcat-total-${cat}`} className="bg-emerald-50/40 border-b border-emerald-100" data-testid={`range-cat-subtotal-${cat}`}>
                                  <td className="py-2 px-4 text-emerald-800 text-xs font-bold italic">↳ {L('Subtotal', 'Zwischensumme')} {cat}</td>
                                  <td className="py-2 px-4 text-center text-emerald-800 text-xs font-bold">{catQty}</td>
                                  <td></td>
                                  <td className="py-2 px-4 text-right text-green-700 text-xs font-bold">{catCash > 0 ? catCash.toFixed(2) : '—'}</td>
                                  <td className="py-2 px-4 text-right text-blue-700 text-xs font-bold">{catCard > 0 ? catCard.toFixed(2) : '—'}</td>
                                  <td className="py-2 px-4 text-right text-emerald-800 text-xs font-extrabold">{catTotal.toFixed(2)} CHF</td>
                                </tr>
                              );
                            });
                            return rows;
                          })()}
                          <tr className="bg-slate-900 text-white font-bold">
                            <td className="py-3 px-4 text-sm">{rangeCatFilter ? `${L('TOTAL', 'GESAMT')} ${catLabel(rangeCatFilter)}` : L('TOTAL (all categories)', 'GESAMT (alle Kategorien)')}</td>
                            <td className="py-3 px-4 text-center text-sm">{rangeItemsShown.reduce((s, it) => s + it.quantity, 0)}</td>
                            <td></td>
                            <td className="py-3 px-4 text-right text-sm">{(rangeCatFilter ? rangeItemsShown.reduce((s, it) => s + it.cash, 0) : rangeSummary.total_cash).toFixed(2)}</td>
                            <td className="py-3 px-4 text-right text-sm">{(rangeCatFilter ? rangeItemsShown.reduce((s, it) => s + it.card, 0) : rangeSummary.total_card).toFixed(2)}</td>
                            <td className="py-3 px-4 text-right text-emerald-300 text-sm">{(rangeCatFilter ? rangeItemsShown.reduce((s, it) => s + it.total, 0) : rangeSummary.total_revenue).toFixed(2)} CHF</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Per-day breakdown */}
                <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden">
                  <div className="p-5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="font-semibold text-slate-800">{L('Daily Breakdown', 'Tägliche Aufschlüsselung')}</h3>
                    <span className="text-xs text-slate-400">
                      {rangeSummary.by_day.filter(d => d.revenue > 0).length} / {rangeSummary.days} {L('active days', 'aktive Tage')}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="range-daily-table">
                      <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Date', 'Datum')}</th>
                        <th className="text-center py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Transactions', 'Transaktionen')}</th>
                        <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Cash', 'Bar')}</th>
                        <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Card', 'Karte')}</th>
                        <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Revenue', 'Umsatz')}</th>
                      </tr></thead>
                      <tbody>
                        {[...rangeSummary.by_day].reverse().map(day => {
                          const hasRevenue = day.revenue > 0;
                          return (
                            <tr
                              key={day.date}
                              className={`border-b border-slate-50 transition-colors cursor-pointer ${hasRevenue ? 'hover:bg-emerald-50/50' : 'hover:bg-slate-50/50 opacity-60'}`}
                              onClick={() => { if (hasRevenue) { setSelectedDate(new Date(day.date)); setActiveTab('history'); } }}
                              data-testid={`range-day-${day.date}`}
                            >
                              <td className="py-3 px-4 text-slate-800 font-medium text-sm">{format(new Date(day.date), 'EEE, dd.MM.yyyy', { locale: dateLocale })}</td>
                              <td className="py-3 px-4 text-center text-slate-600 text-sm">{day.entries}</td>
                              <td className="py-3 px-4 text-right text-sm">{day.cash > 0 ? <span className="text-green-600">{day.cash.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                              <td className="py-3 px-4 text-right text-sm">{day.card > 0 ? <span className="text-blue-600">{day.card.toFixed(2)}</span> : <span className="text-slate-300">—</span>}</td>
                              <td className="py-3 px-4 text-right text-sm font-bold">{hasRevenue ? <span className="text-emerald-600">{day.revenue.toFixed(2)} CHF</span> : <span className="text-slate-300">—</span>}</td>
                            </tr>
                          );
                        })}
                        <tr className="bg-slate-50 font-bold">
                          <td className="py-3 px-4 text-slate-900 text-sm">{L('Total', 'Gesamt')}</td>
                          <td className="py-3 px-4 text-center text-slate-900 text-sm">{rangeSummary.total_entries}</td>
                          <td className="py-3 px-4 text-right text-green-700 text-sm">{rangeSummary.total_cash.toFixed(2)}</td>
                          <td className="py-3 px-4 text-right text-blue-700 text-sm">{rangeSummary.total_card.toFixed(2)}</td>
                          <td className="py-3 px-4 text-right text-emerald-700 text-sm">{rangeSummary.total_revenue.toFixed(2)} CHF</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {!rangeLoading && !rangeSummary && (
              <div className="p-8 text-center text-slate-400">{L('Select a period to view summary', 'Wähle einen Zeitraum für die Übersicht')}</div>
            )}
          </div>
        </TabsContent>

        {/* INVENTORY TAB */}
        <TabsContent value="inventory" className="mt-6">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">{L('Stock Inventory', 'Lagerbestand')}</h2>
              <p className="text-slate-500 text-sm">{L('Manage stock levels. Add items when you purchase them.', 'Lagerbestände verwalten. Artikel hinzufügen bei Einkauf.')}</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => printLagerliste(false)}
                className="bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 font-semibold rounded-lg h-9 px-3 text-sm shadow-sm"
                data-testid="print-lager-staff-btn"
                title={L('Without EK/VK/Margin — for reception', 'Ohne EK/VK/Marge — für Empfang')}
              >
                <Printer size={16} className="mr-1.5" /> {L('Staff PDF', 'Empfang-PDF')}
              </Button>
              {isAdmin && (
                <Button
                  onClick={() => printLagerliste(true)}
                  className="bg-slate-900 text-white hover:bg-slate-800 font-semibold rounded-lg h-9 px-3 text-sm"
                  data-testid="print-lager-btn"
                  title={L('Full version with EK/VK/Margin — admin only', 'Volle Version mit EK/VK/Marge — nur Admin')}
                >
                  <Printer size={16} className="mr-1.5" /> {L('Admin PDF', 'Admin-PDF')}
                </Button>
              )}
            </div>
          </div>
          <div className="mb-5 inline-flex bg-slate-100 rounded-xl p-1 gap-1" data-testid="lager-view-switch">
            {[['stock', L('Stock', 'Bestand')], ['count', L('Stock count', 'Inventur')], ...(isAdmin ? [['recon', L('Reconciliation', 'Lager-Abgleich')]] : [])].map(([k, lbl]) => (
              <button key={k} type="button" onClick={() => setLagerView(k)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${lagerView === k ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`lager-view-${k}`}>{lbl}</button>
            ))}
          </div>
          {lagerView === 'count' && <StockCount API={API} L={L} onDone={loadItems} />}
          {lagerView === 'recon' && isAdmin && <StockReconciliation API={API} L={L} />}
          {lagerView === 'stock' && (<>

          {/* Margin Insights — Admin-only */}
          {isAdmin && (() => {
            const all = items.filter(i => i.is_active);
            const withMargin = [];
            const noEK = [];
            for (const it of all) {
              const vk = Number(it.price) || 0;
              const ek = it.purchase_price != null ? Number(it.purchase_price) : null;
              if (ek == null) {
                noEK.push(it);
              } else if (vk > 0) {
                withMargin.push({ ...it, margin: ((vk - ek) / vk) * 100 });
              }
            }
            const totalCount = all.length;
            const coverage = totalCount > 0 ? (withMargin.length / totalCount) * 100 : 0;
            const top5 = [...withMargin].sort((a, b) => b.margin - a.margin).slice(0, 5);
            const bottom5 = [...withMargin].sort((a, b) => a.margin - b.margin).slice(0, 5);
            const avg = withMargin.length ? withMargin.reduce((s, x) => s + x.margin, 0) / withMargin.length : null;

            const open = marginInsightsOpen;
            const setOpenLocal = setMarginInsightsOpen;

            return (
              <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm mb-4 overflow-hidden" data-testid="margin-insights">
                <button
                  onClick={() => setOpenLocal(!open)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
                  data-testid="margin-insights-toggle"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                      <Percent size={16} weight="duotone" className="text-emerald-600" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-bold text-slate-900">{L('Margin Overview', 'Margen-Übersicht')}</div>
                      <div className="text-xs text-slate-500">
                        {avg != null ? `${L('Avg margin', 'Ø Marge')}: ${avg.toFixed(1)}% · ` : ''}
                        <span className={noEK.length > 0 ? 'text-red-600 font-semibold' : ''}>
                          {noEK.length > 0
                            ? L(`${noEK.length} items without EK`, `${noEK.length} Artikel ohne EK`)
                            : L('All items have EK', 'Alle Artikel haben EK')}
                        </span>
                        {` · ${coverage.toFixed(0)}% ${L('coverage', 'erfasst')}`}
                      </div>
                    </div>
                  </div>
                  <CaretDown size={16} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="px-5 pb-5 pt-1 border-t border-slate-100">
                    {/* Coverage bar */}
                    <div className="mb-5">
                      <div className="flex justify-between text-[11px] text-slate-500 mb-1.5 font-semibold uppercase">
                        <span>{L('EK Coverage', 'EK-Erfassung')}</span>
                        <span>{withMargin.length} / {totalCount}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${coverage >= 80 ? 'bg-emerald-500' : coverage >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${coverage}%` }}
                        />
                      </div>
                    </div>

                    {/* No-EK list (red badges) */}
                    {noEK.length > 0 && (
                      <div className="mb-5">
                        <h4 className="text-xs font-bold uppercase tracking-wide text-red-700 mb-2 flex items-center gap-1.5">
                          <Warning size={14} weight="fill" /> {L('Missing EK — click to add', 'EK fehlt — klicken zum Erfassen')}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {noEK.map(it => (
                            <button
                              key={it.id}
                              onClick={() => handleEditItem(it)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 transition-colors font-semibold"
                              data-testid={`missing-ek-${it.id}`}
                            >
                              <PencilSimple size={11} />
                              {it.name}
                              <span className="text-red-400 text-[10px]">· {Number(it.price || 0).toFixed(2)} CHF</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top + Bottom margins */}
                    {withMargin.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">
                            🏆 {L('Top 5 Best Margins', 'Top 5 Beste Margen')}
                          </h4>
                          <div className="space-y-1.5">
                            {top5.map(it => (
                              <div
                                key={it.id}
                                onClick={() => handleEditItem(it)}
                                className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-50/60 hover:bg-emerald-100/60 cursor-pointer text-sm transition-colors"
                                data-testid={`top-margin-${it.id}`}
                              >
                                <span className="text-slate-800 font-medium truncate">{it.name}</span>
                                <span className="font-black text-emerald-700 tabular-nums ml-3 shrink-0">{it.margin.toFixed(1)}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2">
                            ⚠ {L('5 Weakest Margins', '5 Schwächste Margen')}
                          </h4>
                          <div className="space-y-1.5">
                            {bottom5.map(it => (
                              <div
                                key={it.id}
                                onClick={() => handleEditItem(it)}
                                className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
                                  it.margin < 0
                                    ? 'bg-red-100 hover:bg-red-200'
                                    : it.margin < 30
                                      ? 'bg-red-50 hover:bg-red-100'
                                      : 'bg-amber-50 hover:bg-amber-100'
                                }`}
                                data-testid={`bottom-margin-${it.id}`}
                              >
                                <span className="text-slate-800 font-medium truncate">{it.name}</span>
                                <span className={`font-black tabular-nums ml-3 shrink-0 ${it.margin < 30 ? 'text-red-700' : 'text-amber-700'}`}>{it.margin.toFixed(1)}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="mb-4 flex flex-wrap items-end justify-end gap-3">
            {(() => {
              const all = items.filter(i => i.track_stock);
              const emptyCount = all.filter(it => (it.stock_quantity || 0) <= 0).length;
              const lowCount = all.filter(it => {
                const lt = it.stock_unit === 'liters' ? 2 : 5;
                return (it.stock_quantity || 0) > 0 && (it.stock_quantity || 0) <= lt;
              }).length;
              return (
                <div className="flex gap-2 flex-wrap" data-testid="stock-filter-bar">
                  <button
                    onClick={() => setStockFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${stockFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                    data-testid="stock-filter-all"
                  >
                    {L('All', 'Alle')} <span className="ml-1 opacity-70">{all.length}</span>
                  </button>
                  <button
                    onClick={() => setStockFilter('low')}
                    disabled={lowCount === 0}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${stockFilter === 'low' ? 'bg-amber-500 text-white' : lowCount === 0 ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
                    data-testid="stock-filter-low"
                  >
                    ⚠ {L('Low', 'Niedrig')} <span className="ml-1 opacity-70">{lowCount}</span>
                  </button>
                  <button
                    onClick={() => setStockFilter('empty')}
                    disabled={emptyCount === 0}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${stockFilter === 'empty' ? 'bg-red-500 text-white' : emptyCount === 0 ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
                    data-testid="stock-filter-empty"
                  >
                    ✗ {L('Empty', 'Leer')} <span className="ml-1 opacity-70">{emptyCount}</span>
                  </button>
                  <button
                    onClick={() => setStockFilter('shopping')}
                    disabled={lowCount + emptyCount === 0}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${stockFilter === 'shopping' ? 'bg-emerald-600 text-white' : (lowCount + emptyCount) === 0 ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'}`}
                    data-testid="stock-filter-shopping"
                  >
                    🛒 {L('Shopping list', 'Einkaufsliste')} <span className="ml-1 opacity-70">{lowCount + emptyCount}</span>
                  </button>
                </div>
              );
            })()}
          </div>
          {(() => {
            const stockItems = items.filter(i => i.track_stock).filter(it => {
              if (stockFilter === 'all') return true;
              const lt = it.stock_unit === 'liters' ? 2 : 5;
              const isEmpty = (it.stock_quantity || 0) <= 0;
              const isLow = !isEmpty && (it.stock_quantity || 0) <= lt;
              if (stockFilter === 'empty') return isEmpty;
              if (stockFilter === 'low') return isLow;
              if (stockFilter === 'shopping') return isEmpty || isLow;
              return true;
            });
            if (stockItems.length === 0) {
              return (
                <div className="text-center py-12 text-slate-400">
                  <Package size={48} className="mx-auto mb-4 opacity-30" />
                  <p>{stockFilter === 'all' ? L('No items with stock tracking enabled.', 'Keine Artikel mit Lagerverfolgung aktiviert.') : L('Nothing matches this filter — everything is in stock! 🎉', 'Nichts passt zu diesem Filter — alles auf Lager! 🎉')}</p>
                </div>
              );
            }
            // Group by category
            const grouped = stockItems.reduce((acc, item) => {
              const cat = (item.category && item.category.trim()) || L('Uncategorized', 'Ohne Kategorie');
              if (!acc[cat]) acc[cat] = [];
              acc[cat].push(item);
              return acc;
            }, {});
            const sortedCats = Object.keys(grouped).sort((a, b) => {
              const d = catRank(a) - catRank(b);
              return d !== 0 ? d : a.localeCompare(b);
            });
            return (
              <div className="space-y-6">
                {sortedCats.map(cat => {
                  const catItems = grouped[cat];
                  const totalCount = catItems.length;
                  const lowCount = catItems.filter(it => {
                    const lt = it.stock_unit === 'liters' ? 2 : 5;
                    return (it.stock_quantity || 0) <= lt && (it.stock_quantity || 0) > 0;
                  }).length;
                  const emptyCount = catItems.filter(it => (it.stock_quantity || 0) <= 0).length;
                  return (
                    <div key={cat} className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden" data-testid={`stock-category-${cat}`}>
                      <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200/60">
                        <h3 className="font-bold text-slate-800 text-base tracking-wide">{cat}</h3>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 font-semibold">{totalCount} {L('items', 'Artikel')}</span>
                          {emptyCount > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">{emptyCount} {L('empty', 'leer')}</span>}
                          {lowCount > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">{lowCount} {L('low', 'niedrig')}</span>}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                        {catItems.map(item => {
                          const lowThreshold = item.stock_unit === 'liters' ? 2 : 5;
                          const isLow = (item.stock_quantity || 0) <= lowThreshold;
                          const isEmpty = (item.stock_quantity || 0) <= 0;
                          return (
                            <div key={item.id} className={`bg-white border rounded-xl p-4 ${isEmpty ? 'border-red-200' : isLow ? 'border-amber-200' : 'border-slate-200/60'}`} data-testid={`stock-card-${item.id}`}>
                              <div className="flex items-start justify-between mb-3">
                                <div><p className="text-slate-800 font-medium">{item.name}</p></div>
                                {isEmpty && <span className="px-2 py-0.5 text-xs font-bold bg-red-50 text-red-600 rounded-full">{L('EMPTY', 'LEER')}</span>}
                                {isLow && !isEmpty && <span className="px-2 py-0.5 text-xs font-bold bg-amber-50 text-amber-600 rounded-full">{L('LOW', 'NIEDRIG')}</span>}
                              </div>
                              <div className="flex items-end justify-between">
                                <div>
                                  <p className={`text-3xl font-extrabold ${isEmpty ? 'text-red-500' : isLow ? 'text-amber-500' : 'text-slate-800'}`}>
                                    {item.stock_unit === 'liters' ? `${(item.stock_quantity || 0).toFixed(1)}L` : (item.stock_quantity || 0)}
                                  </p>
                                  <p className="text-slate-400 text-xs">
                                    {item.stock_unit === 'liters' ? L('liters', 'Liter') : L('pieces', 'Stück')}
                                    {item.stock_unit === 'liters' && <span className="text-slate-300 ml-1">({item.stock_deduction || 1}L/{L('sale', 'Verkauf')})</span>}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Button onClick={() => openHistoryFor(item)} className="bg-slate-100 text-slate-600 hover:bg-slate-200 font-semibold text-xs px-2 py-1 h-8 rounded-lg" data-testid={`stock-history-${item.id}`} title={L('History', 'Verlauf')}><Clock size={14} /></Button>
                                  <Button onClick={() => openStockModal(item)} className="bg-emerald-500 text-white hover:bg-emerald-600 font-semibold text-xs px-3 py-1 h-8 rounded-lg" data-testid={`add-stock-${item.id}`}><Plus size={14} className="mr-1" /> {L('Add Stock', 'Einlagern')}</Button>
                                </div>
                              </div>
                              {/* Last restocked date */}
                              <div className="mt-3 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-slate-400" data-testid={`last-restocked-${item.id}`}>
                                <Clock size={11} />
                                {item.last_restocked_at ? (
                                  <span>
                                    {L('Last restock', 'Letzter Einkauf')}:{' '}
                                    <span className="font-semibold text-slate-600">
                                      {new Date(item.last_restocked_at).toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </span>
                                    {item.last_restock_quantity ? (
                                      <span className="text-slate-400 ml-1">
                                        (+{item.stock_unit === 'liters' ? `${Number(item.last_restock_quantity).toFixed(1)}L` : item.last_restock_quantity})
                                      </span>
                                    ) : null}
                                  </span>
                                ) : (
                                  <span className="italic">{L('No restock recorded yet', 'Noch kein Einkauf erfasst')}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          </>)}
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings" className="mt-6">
          {/* Top-level header with BOTH actions visible */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">{L('Settings', 'Einstellungen')}</h2>
              <p className="text-slate-500 text-sm">{L('Manage articles and categories', 'Artikel und Kategorien verwalten')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => { resetItemForm(); setEditingItem(null); setShowItemModal(true); }}
                className="bg-emerald-500 text-white hover:bg-emerald-600 font-semibold rounded-lg" data-testid="add-item-btn">
                <Plus size={18} className="mr-2" /> {L('New Article', 'Neuer Artikel')}
              </Button>
              <Button onClick={() => {
                // Auto-select beer items (kl. Bier / gr. Bier / Herrgöttli) as a sensible default
                const defaults = {};
                items.forEach(it => {
                  const n = (it.name || '').toLowerCase();
                  if (n.includes('kl. bier')) defaults[it.id] = { selected: true, deduction: '0.3' };
                  else if (n.includes('gr. bier')) defaults[it.id] = { selected: true, deduction: '0.5' };
                  else if (n.includes('herrg')) defaults[it.id] = { selected: true, deduction: '0.2' };
                });
                setMergeForm(f => ({ ...f, selected: defaults, total_quantity: '' }));
                setShowMergeModal(true);
              }}
                className="bg-amber-500 text-white hover:bg-amber-600 font-semibold rounded-lg" data-testid="merge-pool-btn">
                <Package size={18} className="mr-2" /> {L('Merge into shared keg', 'Bier-Fass zusammenführen')}
              </Button>
            </div>
          </div>

          {/* Categories first (smaller, faster overview) */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{L('Categories', 'Kategorien')}</h3>
            <Button onClick={() => { setEditingCategory(null); setCategoryName(''); setShowCategoryModal(true); }}
              className="bg-emerald-500 text-white hover:bg-emerald-600 font-semibold rounded-lg h-8 px-3 text-xs" data-testid="add-category-btn">
              <Plus size={14} className="mr-1" /> {L('New Category', 'Neue Kategorie')}
            </Button>
          </div>
          <div className="space-y-3 mb-8" data-testid="categories-list">
            {(() => {
              // Compute orphan categories: any distinct item.category not
              // present in the categories table. Rendered as extra pseudo-
              // rows so items are never invisible even if a rename got
              // stuck mid-migration.
              const knownNames = new Set((categoriesList || []).map(c => c.name));
              const orphanNames = Array.from(new Set(
                (items || []).map(i => i.category).filter(n => n && !knownNames.has(n))
              ));
              const orphanCats = orphanNames.map((n, i) => ({ id: `_orphan_${i}`, name: n, orphan: true }));
              const combined = [...(categoriesList || []), ...orphanCats];
              if (combined.length === 0) {
                return (
                  <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm p-6 text-center text-slate-400 italic text-sm">
                    {L('No categories yet', 'Noch keine Kategorien')}
                  </div>
                );
              }
              return combined.map((cat, catIdx) => {
              const itemsInCat = items.filter(i => i.category === cat.name);
              const activeCount = itemsInCat.filter(i => i.is_active).length;
              const isFirst = catIdx === 0;
              const isLast = catIdx === combined.length - 1;
              return (
                <div key={cat.id} className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden" data-testid={`category-row-${cat.name}`}>
                  <div className="flex items-center justify-between px-5 py-3 bg-slate-50/60 border-b border-slate-100">
                    <div className="flex items-center gap-3 min-w-0">
                      <h4 className="text-base font-bold text-slate-900 truncate">{catLabel(cat.name)}</h4>
                      {cat.orphan && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title={L('Items reference this category name but it is not in the categories table yet', 'Artikel referenzieren diesen Kategorienamen, er fehlt aber in der Kategorien-Tabelle')}>
                          {L('auto', 'auto')}
                        </span>
                      )}
                      <span className="text-xs text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {activeCount} {L('active', 'aktiv')}
                        {itemsInCat.length > activeCount && <span className="text-slate-400"> · {itemsInCat.length - activeCount} {L('inactive', 'inaktiv')}</span>}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveCategory(catIdx, -1)}
                        disabled={isFirst || cat.orphan}
                        className="p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent rounded-lg"
                        data-testid={`move-up-category-${cat.name}`}
                        title={L('Move up', 'Nach oben')}
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        onClick={() => moveCategory(catIdx, +1)}
                        disabled={isLast || cat.orphan}
                        className="p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent rounded-lg"
                        data-testid={`move-down-category-${cat.name}`}
                        title={L('Move down', 'Nach unten')}
                      >
                        <ArrowDown size={16} />
                      </button>
                      {!cat.orphan && (<>
                      <button onClick={() => { setEditingCategory(cat); setCategoryName(cat.name); setShowCategoryModal(true); }}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg" data-testid={`edit-category-${cat.name}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => handleDeleteCategory(cat)}
                        className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" data-testid={`delete-category-${cat.name}`}>
                        <Trash size={16} />
                      </button>
                      </>)}
                    </div>
                  </div>
                  {itemsInCat.length === 0 ? (
                    <p className="px-5 py-3 text-xs text-slate-400 italic">
                      {L('No articles in this category yet', 'Noch keine Artikel in dieser Kategorie')}
                    </p>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {[...itemsInCat]
                        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de', { sensitivity: 'base' }))
                        .map(item => (
                        <li
                          key={item.id}
                          onClick={() => handleEditItem(item)}
                          className={`flex items-center justify-between px-5 py-2 text-sm cursor-pointer hover:bg-emerald-50/60 transition-colors ${item.is_active ? '' : 'opacity-50'}`}
                          data-testid={`category-item-${item.id}`}
                          title={L('Click to edit', 'Klicken zum Bearbeiten')}
                        >
                          <span className="text-slate-800 font-medium truncate flex items-center gap-2">
                            <PencilSimple size={12} className="text-slate-300 shrink-0" />
                            {item.name}
                            {!item.is_active && <span className="ml-1 text-[10px] uppercase font-bold text-amber-500">({L('inactive', 'inaktiv')})</span>}
                          </span>
                          <span className="text-slate-600 font-semibold whitespace-nowrap ml-3">
                            {item.price.toFixed(2)} CHF
                            {item.track_stock && <span className="text-xs text-slate-400 ml-2">· {Number(item.stock_quantity || 0).toFixed(item.stock_unit === 'liters' ? 1 : 0)} {item.stock_unit === 'liters' ? 'L' : ''}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
              });
            })()}
          </div>

          {/* Articles below */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{L('Articles', 'Artikel')}</h3>
            <span className="text-[11px] text-slate-400">{L('Tap a row to edit', 'Zeile antippen zum Bearbeiten')}</span>
          </div>
          <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full" data-testid="items-table">
              <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Article', 'Article')}</th>
                <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Category', 'Category')}</th>
                <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Price', 'Price')}</th>
                <th className="text-center py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Stock', 'Stock')}</th>
                <th className="text-center py-3 px-4 text-xs text-slate-400 uppercase font-semibold">Status</th>
                <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase font-semibold">{L('Actions', 'Actions')}</th>
              </tr></thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => handleEditItem(item)}
                    className="border-b border-slate-50 hover:bg-emerald-50/50 cursor-pointer"
                    data-testid={`settings-row-${item.id}`}
                  >
                    <td className="py-3 px-4 text-slate-800 font-medium text-sm">{item.name}</td>
                    <td className="py-3 px-4 text-slate-500 text-sm">{item.category}</td>
                    <td className="py-3 px-4 text-right text-emerald-600 font-bold text-sm">{item.price.toFixed(2)} CHF</td>
                    <td className="py-3 px-4 text-center">{item.track_stock ? (<span className={`font-bold text-sm ${(item.stock_quantity || 0) <= 0 ? 'text-red-500' : (item.stock_quantity || 0) <= (item.stock_unit === 'liters' ? 2 : 5) ? 'text-amber-500' : 'text-slate-800'}`}>{item.stock_unit === 'liters' ? `${(item.stock_quantity || 0).toFixed(1)}L` : (item.stock_quantity || 0)}{item.stock_unit === 'liters' ? <span className="text-[10px] text-slate-400 ml-0.5">({item.stock_deduction}L)</span> : ''}</span>) : (<span className="text-slate-300 text-xs">--</span>)}</td>
                    <td className="py-3 px-4 text-center"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{item.is_active ? L('Active', 'Active') : L('Inactive', 'Inactive')}</span></td>
                    <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}><div className="flex justify-end gap-1">
                      {item.track_stock && <button onClick={() => openStockModal(item)} className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-lg" data-testid={`settings-stock-${item.id}`}><Package size={16} /></button>}
                      <button onClick={() => handleEditItem(item)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg" data-testid={`edit-item-${item.id}`}><PencilSimple size={16} /></button>
                      <button onClick={() => handleDeleteItem(item.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" data-testid={`delete-item-${item.id}`}><Trash size={16} /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-md rounded-2xl" data-testid="item-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">{editingItem ? L('Edit Article', 'Edit Article') : L('New Article', 'New Article')}</DialogTitle>
            <DialogDescription className="text-slate-500">{editingItem ? L('Update article details', 'Update article details') : L('Add a new article to the register', 'Add a new article to the register')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2"><Label className="text-xs text-slate-500 uppercase font-semibold">{L('Name', 'Name')} *</Label><Input value={itemForm.name} onChange={(e) => setItemForm({...itemForm, name: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg" placeholder="Softdrink" data-testid="item-name" /></div>
            <div className={`grid ${isAdmin ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
              <div className="space-y-2">
                <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Sale Price VK (CHF)', 'Verkaufspreis VK (CHF)')} *</Label>
                <Input type="number" step="0.50" value={itemForm.price} onChange={(e) => setItemForm({...itemForm, price: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg" placeholder="4.00" data-testid="item-price" />
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Cost Price EK (CHF)', 'Einkaufspreis EK (CHF)')}</Label>
                  <Input type="number" step="0.01" value={itemForm.purchase_price} onChange={(e) => setItemForm({...itemForm, purchase_price: e.target.value})} className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg" placeholder="1.20" data-testid="item-purchase-price" />
                  {(() => {
                    const vk = parseFloat(itemForm.price);
                    const ek = parseFloat(itemForm.purchase_price);
                    if (isFinite(vk) && isFinite(ek) && vk > 0 && ek >= 0) {
                      const margin = ((vk - ek) / vk) * 100;
                      const markup = ek > 0 ? ((vk - ek) / ek) * 100 : null;
                      return (
                        <p className="text-[10px] text-emerald-600 font-semibold">
                          {L('Margin', 'Marge')}: {margin.toFixed(1)}%{markup != null ? ` · ${L('Markup', 'Aufschlag')}: ${markup.toFixed(0)}%` : ''}
                        </p>
                      );
                    }
                    return <p className="text-[10px] text-slate-400">{L('Optional — used for margin in the Lagerliste PDF', 'Optional — für die Marge in der Lagerliste-PDF')}</p>;
                  })()}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Category', 'Kategorie')} *</Label>
              <div className="flex flex-wrap gap-1.5">
                {categoriesList.map(cat => (
                  <button key={cat.id} type="button"
                    onClick={() => setItemForm({...itemForm, category: cat.name})}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${itemForm.category === cat.name ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`}
                    data-testid={`cat-quick-${cat.name}`}>
                    {catLabel(cat.name)}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 leading-tight">{L('Manage categories under "Kategorien" tab in Settings.', 'Kategorien verwalten unter "Kategorien" im Einstellungs-Tab.')}</p>
            </div>
            <div className="flex items-center justify-between">
              <div><Label className="text-xs text-slate-500 uppercase font-semibold">{L('Track Stock', 'Track Stock')}</Label><p className="text-slate-400 text-xs mt-0.5">{L('Auto-deduct when sold', 'Auto-deduct when sold')}</p></div>
              <button onClick={() => setItemForm({...itemForm, track_stock: !itemForm.track_stock})} className={`w-12 h-6 rounded-full transition-colors ${itemForm.track_stock ? 'bg-emerald-500' : 'bg-slate-200'}`} data-testid="toggle-track-stock"><div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${itemForm.track_stock ? 'translate-x-6' : 'translate-x-0.5'}`} /></button>
            </div>
            {itemForm.track_stock && (
              <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
                {/* Stock pool selector — share stock with another tracked item (e.g. one keg → small + large beer) */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 uppercase font-semibold">
                    {L('Shares stock with', 'Lager geteilt mit')}
                  </Label>
                  <select
                    value={itemForm.stock_pool_id}
                    onChange={(e) => setItemForm({ ...itemForm, stock_pool_id: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    data-testid="item-stock-pool"
                  >
                    <option value="">{L('-- Own stock (separate) --', '-- Eigenes Lager (separat) --')}</option>
                    {items
                      .filter(it => it.track_stock && !it.stock_pool_id && it.id !== editingItem?.id)
                      .map(it => (
                        <option key={it.id} value={it.id}>
                          {it.name} ({(it.stock_quantity || 0).toFixed(it.stock_unit === 'liters' ? 1 : 0)}{it.stock_unit === 'liters' ? 'L' : ''})
                        </option>
                      ))}
                  </select>
                  <p className="text-[10px] text-slate-400">
                    {L(
                      'E.g. 1× Lager keg (master) → "Bier 0.3L" and "Bier 0.5L" both share the keg. Each sale deducts the per-sale amount from the keg.',
                      'Z.B. 1× Lager-Fass (Master) → "Bier 0.3L" und "Bier 0.5L" teilen sich das Fass. Jeder Verkauf zieht den Pro-Verkauf-Betrag vom Fass ab.',
                    )}
                  </p>
                </div>

                {!itemForm.stock_pool_id && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Unit', 'Einheit')}</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setItemForm({...itemForm, stock_unit: 'pcs', stock_deduction: '1'})}
                          className={`p-2 rounded-lg text-sm font-medium border-2 transition-all ${itemForm.stock_unit === 'pcs' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-600'}`}
                          data-testid="unit-pcs">
                          {L('Pieces', 'Stück')} (1, 2, 3...)
                        </button>
                        <button onClick={() => setItemForm({...itemForm, stock_unit: 'liters', stock_deduction: '0.3'})}
                          className={`p-2 rounded-lg text-sm font-medium border-2 transition-all ${itemForm.stock_unit === 'liters' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}
                          data-testid="unit-liters">
                          {L('Liters', 'Liter')} (L)
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Current Stock', 'Aktueller Bestand')}</Label>
                        <Input type="number" step={itemForm.stock_unit === 'liters' ? '0.1' : '1'} value={itemForm.stock_quantity} onChange={(e) => setItemForm({...itemForm, stock_quantity: e.target.value})} className="bg-white border-slate-200 text-slate-900 rounded-lg" placeholder="0" data-testid="item-stock" />
                        {itemForm.stock_unit === 'liters' && <p className="text-[10px] text-slate-400">{L('in liters', 'in Liter')}</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Per Sale', 'Pro Verkauf')}</Label>
                        <Input type="number" step="0.1" value={itemForm.stock_deduction} onChange={(e) => setItemForm({...itemForm, stock_deduction: e.target.value})} className="bg-white border-slate-200 text-slate-900 rounded-lg" placeholder="1" data-testid="item-deduction" />
                        {itemForm.stock_unit === 'liters' && <p className="text-[10px] text-slate-400">{L('e.g. 0.3L small, 0.5L large', 'z.B. 0.3L klein, 0.5L gross')}</p>}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Minimum stock (email alert)', 'Mindestbestand (E-Mail-Warnung)')}</Label>
                      <Input type="number" min="0" step={itemForm.stock_unit === 'liters' ? '0.1' : '1'} value={itemForm.min_stock} onChange={(e) => setItemForm({...itemForm, min_stock: e.target.value})} className="bg-white border-slate-200 text-slate-900 rounded-lg" placeholder={L('e.g. 24 — empty = no alert', 'z.B. 24 — leer = keine Warnung')} data-testid="item-min-stock" />
                    </div>
                  </>
                )}

                {itemForm.stock_pool_id && (
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Per Sale', 'Pro Verkauf')}</Label>
                    <Input type="number" step="0.1" value={itemForm.stock_deduction} onChange={(e) => setItemForm({...itemForm, stock_deduction: e.target.value})} className="bg-white border-slate-200 text-slate-900 rounded-lg" placeholder="0.3" data-testid="item-deduction-pool" />
                    <p className="text-[10px] text-slate-400">
                      {L('How much to deduct from the shared keg per sale (e.g. 0.3L for a small beer, 0.5L for a large).',
                         'Wie viel pro Verkauf vom geteilten Fass abgezogen wird (z.B. 0.3L für ein kleines Bier, 0.5L für ein grosses).')}
                    </p>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Active', 'Active')}</Label>
              <button onClick={() => setItemForm({...itemForm, is_active: !itemForm.is_active})} className={`w-12 h-6 rounded-full transition-colors ${itemForm.is_active ? 'bg-green-500' : 'bg-slate-200'}`} data-testid="toggle-active"><div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${itemForm.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} /></button>
            </div>

            {/* Size Variants — only for existing (saved) items. New items must
                be created first, then this panel appears on the second edit. */}
            {editingItem && (
              <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="variants-section">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-purple-700 uppercase font-bold">
                    {L('Sizes / Variants', 'Grössen / Varianten')}
                  </Label>
                  <span className="text-[10px] text-slate-400">
                    {L('Optional — turns this into one card with a size picker', 'Optional — macht daraus eine Kachel mit Grössenauswahl')}
                  </span>
                </div>
                {/* Existing variants */}
                {(editingItem.variants || []).length > 0 && (
                  <div className="space-y-1.5">
                    {editingItem.variants.map(v => (
                      <div key={v.id} className="flex items-center gap-2 bg-purple-50/40 border border-purple-100 rounded-lg px-3 py-1.5 text-sm" data-testid={`variant-row-${v.variant_label}`}>
                        <span className="font-bold text-slate-800 w-12">{v.variant_label}</span>
                        <span className="text-slate-500 flex-1">
                          {v.stock_quantity == null ? '∞' : `${Number(v.stock_quantity).toFixed(0)} Stk`}
                        </span>
                        <button
                          onClick={() => unlinkVariant(v.id)}
                          className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                          title={L('Turn back into standalone item (keeps stock)', 'Wieder als eigenständigen Artikel führen (Bestand bleibt)')}
                          data-testid={`unlink-variant-${v.variant_label}`}
                        >
                          {L('unlink', 'trennen')}
                        </button>
                        <button
                          onClick={() => removeVariant(v.id, v.variant_label)}
                          className="text-red-400 hover:text-red-600"
                          data-testid={`delete-variant-${v.variant_label}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Add new size */}
                <div className="flex gap-2">
                  <Input
                    value={newVariant.label}
                    onChange={(e) => setNewVariant({ ...newVariant, label: e.target.value })}
                    placeholder={L('Size (e.g. 40)', 'Grösse (z.B. 40)')}
                    className="flex-1 bg-slate-50 border-slate-200 text-slate-900 rounded-lg h-9 text-sm"
                    data-testid="new-variant-label"
                  />
                  <Input
                    type="number"
                    min="0"
                    value={newVariant.stock}
                    onChange={(e) => setNewVariant({ ...newVariant, stock: e.target.value })}
                    placeholder={L('Stock', 'Bestand')}
                    className="w-24 bg-slate-50 border-slate-200 text-slate-900 rounded-lg h-9 text-sm"
                    data-testid="new-variant-stock"
                  />
                  <Button onClick={addNewVariant} className="bg-purple-500 hover:bg-purple-600 text-white h-9 rounded-lg text-sm" data-testid="add-variant-btn">
                    <Plus size={14} />
                  </Button>
                </div>
                {/* Link existing item as a variant */}
                <div className="pt-1">
                  <details className="text-xs text-slate-500">
                    <summary className="cursor-pointer hover:text-slate-700">
                      {L('… or link an existing item as a size', '… oder bestehenden Artikel als Grösse einhängen')}
                    </summary>
                    <div className="mt-2 flex gap-2">
                      <select
                        className="flex-1 bg-slate-50 border border-slate-200 text-slate-900 rounded-lg h-9 text-sm px-2"
                        data-testid="link-existing-select"
                        onChange={(e) => {
                          const id = e.target.value;
                          if (!id) return;
                          const item = items.find(i => i.id === id);
                          const suggestedLabel = window.prompt(
                            L('Size label for this item?', 'Grösse für diesen Artikel?'),
                            item?.name?.match(/\d+/)?.[0] || '',
                          );
                          if (suggestedLabel) linkAsVariant(id, suggestedLabel);
                          e.target.value = '';
                        }}
                      >
                        <option value="">{L('— pick an existing item —', '— bestehenden Artikel wählen —')}</option>
                        {items
                          .filter(i => i.id !== editingItem.id && !i.has_variants && !i.variant_of && i.category === editingItem.category)
                          .map(i => (
                            <option key={i.id} value={i.id}>
                              {i.name} {i.track_stock ? ` · ${i.stock_quantity ?? 0} Stk` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                      {L(
                        "Only items in the same category and without their own variants show up. Their stock is preserved.",
                        'Es erscheinen nur Artikel derselben Kategorie ohne eigene Varianten. Der Bestand bleibt erhalten.',
                      )}
                    </p>
                  </details>
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowItemModal(false)} className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg">{L('Cancel', 'Cancel')}</Button>
              <Button onClick={handleSaveItem} disabled={!itemForm.name || !itemForm.price || !itemForm.category} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold rounded-lg" data-testid="save-item-btn">{L('Save', 'Save')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Modal */}
      <Dialog open={showStockModal} onOpenChange={setShowStockModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-sm rounded-2xl" data-testid="stock-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">{L('Update Stock', 'Lager aktualisieren')}</DialogTitle>
            <DialogDescription className="text-slate-500">{L('Add or remove stock for this item', 'Lagerbestand für diesen Artikel anpassen')}</DialogDescription>
          </DialogHeader>
          {stockItem && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 p-3 rounded-xl">
                <p className="text-slate-800 font-medium">{stockItem.name}</p>
                <p className="text-slate-500 text-sm">{L('Current stock:', 'Aktueller Bestand:')} <span className="text-slate-800 font-bold">{stockItem.stock_unit === 'liters' ? `${(stockItem.stock_quantity || 0).toFixed(1)}L` : (stockItem.stock_quantity || 0)}</span></p>
              </div>
              <div className="space-y-2"><Label className="text-xs text-slate-500 uppercase font-semibold">{L('Quantity to add', 'Menge hinzufügen')} {stockItem?.stock_unit === 'liters' ? '(L)' : ''}</Label><Input type="number" step={stockItem?.stock_unit === 'liters' ? '0.1' : '1'} value={stockAmount} onChange={(e) => setStockAmount(e.target.value)} className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg" placeholder={stockItem?.stock_unit === 'liters' ? '10.0' : '10'} data-testid="stock-quantity" /><p className="text-slate-400 text-xs">{L('Use negative number to remove stock', 'Negative Zahl zum Entfernen verwenden')}</p></div>
              <div className="space-y-2"><Label className="text-xs text-slate-500 uppercase font-semibold">{L('Reason', 'Grund')}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[{ key: 'purchase', label: L('Purchase', 'Einkauf'), icon: ArrowUp }, { key: 'adjustment', label: L('Adjust', 'Korrektur'), icon: PencilSimple }, { key: 'damage', label: L('Damage', 'Schaden'), icon: ArrowDown }].map(({ key, label, icon: Icon }) => (
                    <button key={key} onClick={() => setStockReason(key)} className={`p-2 border rounded-lg flex flex-col items-center gap-1 transition-all text-xs ${stockReason === key ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`stock-reason-${key}`}>
                      <Icon size={16} className={stockReason === key ? 'text-emerald-600' : 'text-slate-400'} /><span className="text-slate-700 font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {stockAmount && <div className="bg-slate-50 p-3 rounded-xl text-center"><p className="text-slate-400 text-xs">{L('New stock level', 'Neuer Bestand')}</p><p className="text-2xl font-extrabold text-slate-800">{stockItem?.stock_unit === 'liters' ? `${((stockItem.stock_quantity || 0) + (parseFloat(stockAmount) || 0)).toFixed(1)}L` : (stockItem.stock_quantity || 0) + (parseFloat(stockAmount) || 0)}</p></div>}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowStockModal(false)} className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg">{L('Cancel', 'Abbrechen')}</Button>
                <Button onClick={handleStockUpdate} disabled={!stockAmount || stockAmount === '0'} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold rounded-lg" data-testid="confirm-stock-btn">{L('Update', 'Aktualisieren')}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Split Modal */}
      <Dialog open={showPaymentSplit} onOpenChange={setShowPaymentSplit}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-sm rounded-2xl" data-testid="payment-split-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">{L('Payment', 'Zahlung')}</DialogTitle>
            <DialogDescription className="text-slate-500">{L('Split between cash and card', 'Auf Bar und Karte aufteilen')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-slate-50 p-4 rounded-xl text-center">
              <p className="text-slate-400 text-xs uppercase font-semibold mb-1">Total</p>
              <p className="text-3xl font-extrabold text-emerald-600">{getCartTotal().toFixed(2)} CHF</p>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-2">
                  <Money size={14} className="text-green-600" /> {L('Cash', 'Bar')} (CHF)
                </Label>
                <Input
                  type="number" step="0.10" min="0"
                  value={splitCash}
                  onChange={(e) => handleSplitCashChange(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg text-lg font-bold"
                  data-testid="split-cash"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 uppercase font-semibold flex items-center gap-2">
                  <CreditCard size={14} className="text-blue-600" /> {L('Card', 'Karte')} (CHF)
                </Label>
                <Input
                  type="number" step="0.10" min="0"
                  value={splitCard}
                  onChange={(e) => handleSplitCardChange(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg text-lg font-bold"
                  data-testid="split-card"
                />
              </div>
            </div>

            <TipInput value={tipAmount} onChange={setTipAmount} base={parseFloat(splitCard) || getCartTotal()} />
            {(parseFloat(tipAmount) || 0) > 0 && (
              <p className="text-sm text-slate-600 text-center" data-testid="card-total-with-tip">
                {L('Card total', 'Karte total')}: <b>{((parseFloat(splitCard) || 0) + (parseFloat(tipAmount) || 0)).toFixed(2)} CHF</b>
              </p>
            )}

            {/* Quick buttons */}
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => { setSplitCash(getCartTotal().toFixed(2)); setSplitCard('0.00'); }}
                className="p-2 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-green-400 hover:bg-green-50 transition-all" data-testid="quick-all-cash">
                100% {L('Cash', 'Bar')}
              </button>
              <button onClick={() => { setSplitCash('0.00'); setSplitCard(getCartTotal().toFixed(2)); }}
                className="p-2 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-blue-400 hover:bg-blue-50 transition-all" data-testid="quick-all-card">
                100% {L('Card', 'Karte')}
              </button>
              <button onClick={() => { const half = (getCartTotal() / 2).toFixed(2); setSplitCash(half); setSplitCard((getCartTotal() - parseFloat(half)).toFixed(2)); }}
                className="p-2 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-purple-400 hover:bg-purple-50 transition-all" data-testid="quick-half">
                50/50
              </button>
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowPaymentSplit(false)} className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg">
                {L('Cancel', 'Abbrechen')}
              </Button>
              <Button onClick={confirmPayment} disabled={checkoutLoading} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold rounded-lg" data-testid="confirm-payment-btn">
                <Check size={16} className="mr-1" /> {checkoutLoading ? '...' : L('Confirm', 'Bestätigen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Confirm', 'Bestätigen')}</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 mt-2">{confirmAction?.message}</p>
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
            <Button onClick={() => confirmAction?.onConfirm()} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="confirm-delete-btn">
              {L('Delete', 'Löschen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Size Variant Picker — opens when clicking a master item with sizes */}
      <Dialog open={!!variantPicker} onOpenChange={(open) => { if (!open) setVariantPicker(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-md" data-testid="variant-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{variantPicker?.name}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {L('Pick a size — greyed-out sizes are out of stock.', 'Grösse wählen — ausgegraute Grössen sind ausverkauft.')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
            {(variantPicker?.variants || []).map(v => {
              const inStock = v.stock_quantity == null || Number(v.stock_quantity) > 0;
              const disabled = !inStock || v.is_active === false;
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    addToCart(variantPicker, v, !!variantPicker._custom);
                    setVariantPicker(null);
                  }}
                  data-testid={`variant-pick-${v.variant_label}`}
                  className={`relative flex flex-col items-center justify-center rounded-xl border-2 py-3 transition font-bold ${
                    disabled
                      ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-400 shadow-sm'
                  }`}
                >
                  <span className="text-xl leading-none">{v.variant_label}</span>
                  <span className="text-[10px] font-medium mt-1 uppercase tracking-wider">
                    {v.stock_quantity == null ? '∞' : `${Number(v.stock_quantity).toFixed(0)} ${L('left', 'Stk')}`}
                  </span>
                </button>
              );
            })}
            {(!variantPicker?.variants || variantPicker.variants.length === 0) && (
              <p className="col-span-full text-center text-sm text-slate-400 py-8">
                {L('No sizes configured yet.', 'Noch keine Grössen konfiguriert.')}
              </p>
            )}
          </div>
          <div className="pt-4 border-t border-slate-100 mt-2">
            <Button variant="outline" onClick={() => setVariantPicker(null)} className="w-full border-slate-200 text-slate-600">
              {L('Cancel', 'Abbrechen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Racket Sale Price Prompt */}
      <Dialog open={!!racketPrompt} onOpenChange={(open) => { if (!open) setRacketPrompt(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-sm" data-testid="racket-price-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Sale Price', 'Verkaufspreis')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {racketPrompt?.item?.name}
              {racketPrompt?.item?.price > 0 && (
                <span className="block text-xs text-slate-400 mt-1">
                  {L('Retail', 'UVP')}: {Number(racketPrompt.item.price).toFixed(2)} CHF
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Label className="label-text">{L('Final price (CHF)', 'Finaler Preis (CHF)')}</Label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.5"
              min="0"
              autoFocus
              value={racketPrompt?.price || ''}
              onChange={(e) => setRacketPrompt(prev => prev ? { ...prev, price: e.target.value } : null)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmRacketPrice(); }}
              className="text-lg font-bold text-slate-900 bg-slate-50 border-slate-200"
              placeholder="0.00"
              data-testid="racket-price-input"
            />
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setRacketPrompt(null)} className="flex-1 border-slate-200 text-slate-600">
                {L('Cancel', 'Abbrechen')}
              </Button>
              <Button onClick={confirmRacketPrice} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="racket-price-confirm">
                {L('Add to Cart', 'In Kasse')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send to Accounting Dialog */}
      <Dialog open={showAccountingDialog} onOpenChange={(open) => { if (!open) { setShowAccountingDialog(false); setAccountingResult(null); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-md" data-testid="accounting-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Send to Swiss Accounting', 'An Schweizer Buchhaltung senden')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {L('Push a summarized daily report to your external accounting system.', 'Summierter Tagesbericht wird an dein externes Buchhaltungssystem gesendet.')}
            </DialogDescription>
          </DialogHeader>

          {!accountingResult ? (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700 space-y-1">
                <p><span className="font-semibold">{L('Date', 'Datum')}:</span> {format(selectedDate, 'dd.MM.yyyy')}</p>
                {summary && <>
                  <p><span className="font-semibold">{L('Total Revenue', 'Gesamtumsatz')}:</span> CHF {summary.total_revenue.toFixed(2)}</p>
                  <p><span className="font-semibold">{L('Cash', 'Bar')}:</span> CHF {(summary.total_cash || 0).toFixed(2)}</p>
                  <p><span className="font-semibold">{L('Card', 'Karte')}:</span> CHF {(summary.total_card || 0).toFixed(2)}</p>
                  <p><span className="font-semibold">{L('Transactions', 'Transaktionen')}:</span> {summary.total_entries}</p>
                </>}
              </div>
              <p className="text-xs text-slate-500">
                {L('Entries will be grouped per account and payment type (one journal entry per group).', 'Einträge werden pro Konto und Zahlungsart gruppiert (ein Buchungssatz pro Gruppe).')}
              </p>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowAccountingDialog(false)} className="flex-1 border-slate-200 text-slate-600" data-testid="accounting-cancel-btn">
                  {L('Cancel', 'Abbrechen')}
                </Button>
                <Button onClick={sendToAccounting} disabled={exportLoading} className="flex-1 bg-blue-600 text-white hover:bg-blue-700 font-bold" data-testid="accounting-confirm-btn">
                  {exportLoading ? '...' : L('Send now', 'Jetzt senden')}
                </Button>
              </div>
            </div>
          ) : accountingResult.ok ? (
            <div className="space-y-4 mt-2" data-testid="accounting-result-success">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm space-y-1">
                <p className="font-bold text-emerald-700">{L('Successfully sent', 'Erfolgreich gesendet')}</p>
                <p><span className="font-semibold">{L('Reference', 'Referenz')}:</span> {accountingResult.referenz}</p>
                <p><span className="font-semibold">{L('Entries pushed', 'Einträge übertragen')}:</span> {accountingResult.entries_pushed}</p>
                {accountingResult.entries_failed > 0 && (
                  <p className="text-amber-700"><span className="font-semibold">{L('Failed', 'Fehlgeschlagen')}:</span> {accountingResult.entries_failed}</p>
                )}
                <p><span className="font-semibold">Total:</span> CHF {accountingResult.grand_total.toFixed(2)}</p>
                {accountingResult.previously_exported && (
                  <p className="text-amber-700 text-xs mt-2">{L('Note: This day was already exported before.', 'Hinweis: Dieser Tag wurde bereits zuvor exportiert.')}</p>
                )}
              </div>
              <Button onClick={() => { setShowAccountingDialog(false); setAccountingResult(null); }} className="w-full bg-slate-800 text-white hover:bg-slate-900 font-bold" data-testid="accounting-close-btn">
                {L('Close', 'Schliessen')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-2" data-testid="accounting-result-error">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm">
                <p className="font-bold text-red-700 mb-1">{L('Failed', 'Fehlgeschlagen')}</p>
                <p className="text-red-600">{accountingResult.error}</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setShowAccountingDialog(false); setAccountingResult(null); }} className="flex-1 border-slate-200 text-slate-600">
                  {L('Close', 'Schliessen')}
                </Button>
                <Button onClick={sendToAccounting} disabled={exportLoading} className="flex-1 bg-blue-600 text-white hover:bg-blue-700 font-bold" data-testid="accounting-retry-btn">
                  {L('Retry', 'Erneut versuchen')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Category create/edit modal */}
      <Dialog open={showCategoryModal} onOpenChange={(o) => { if (!o) { setShowCategoryModal(false); setEditingCategory(null); setCategoryName(''); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900 max-w-sm rounded-2xl" data-testid="category-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">{editingCategory ? L('Edit Category', 'Kategorie bearbeiten') : L('New Category', 'Neue Kategorie')}</DialogTitle>
            <DialogDescription className="text-slate-500">{editingCategory ? L('Renaming will also update all articles and report entries.', 'Umbenennen aktualisiert alle Artikel und Berichts-Einträge.') : L('Create a new category for articles.', 'Eine neue Kategorie für Artikel anlegen.')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-500 uppercase font-semibold">{L('Name', 'Name')} *</Label>
              <Input value={categoryName} onChange={(e) => setCategoryName(e.target.value)}
                className="bg-slate-50 border-slate-200 text-slate-900 rounded-lg" placeholder="z.B. Pizza" data-testid="category-name" autoFocus />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={() => { setShowCategoryModal(false); setEditingCategory(null); setCategoryName(''); }}
              className="flex-1 border-slate-200 text-slate-600 rounded-lg" data-testid="cancel-category-btn">
              {L('Cancel', 'Abbrechen')}
            </Button>
            <Button onClick={handleSaveCategory} disabled={!categoryName.trim()}
              className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold rounded-lg" data-testid="save-category-btn">
              {L('Save', 'Speichern')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Stock history dialog (per-item: restocks + sales) ====== */}
      <Dialog open={!!historyItem} onOpenChange={(o) => { if (!o) setHistoryItem(null); }}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="stock-history-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {L('Stock history', 'Lager-Verlauf')} — {historyItem?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              {historyItem?.stock_pool_id
                ? L('This item draws from a shared pool. Showing the pool master\'s restocks + all member sales.', 'Dieser Artikel teilt sich ein Pool-Lager. Anzeige: Einkäufe des Pool-Masters + alle Verkäufe der Pool-Mitglieder.')
                : L('Everything that went in (restocks) and out (sales) of this item.', 'Alles was rein- (Einkauf) und rausging (Verkauf) bei diesem Artikel.')
              }
            </DialogDescription>
          </DialogHeader>

          {historyLoading && (
            <div className="py-6 text-center text-slate-400 text-sm">{L('Loading…', 'Lädt…')}</div>
          )}

          {!historyLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              {/* Restocks */}
              <div className="rounded-lg border border-emerald-200 overflow-hidden">
                <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between">
                  <div className="text-xs uppercase font-bold text-emerald-800">
                    📦 {L('Restocks', 'Einkäufe')}
                  </div>
                  <div className="text-[11px] text-emerald-700 font-semibold">
                    {historyMovements.length} {L('events', 'Ereignisse')}
                  </div>
                </div>
                <div className="max-h-[400px] overflow-y-auto divide-y divide-emerald-100 text-sm">
                  {historyMovements.length === 0 && (
                    <div className="px-3 py-6 text-center text-slate-400 italic text-xs">
                      {L('No restocks recorded yet.', 'Noch keine Einkäufe erfasst.')}
                    </div>
                  )}
                  {historyMovements.map((m) => {
                    const date = m.created_at ? new Date(m.created_at) : null;
                    const change = Number(m.quantity_change || 0);
                    const unit = historyItem?.stock_unit === 'liters' ? 'L' : '';
                    return (
                      <div key={m.id} className="px-3 py-2 flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-700 font-semibold text-xs">
                            {date ? date.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                            <span className="text-slate-400 ml-1.5">{date ? date.toLocaleTimeString(language === 'de' ? 'de-CH' : 'en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {m.created_by_name || '—'}
                            {m.reason && <span className="ml-1 text-slate-300">· {m.reason}</span>}
                          </div>
                        </div>
                        <div className={`text-base font-extrabold tabular-nums ${change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {change >= 0 ? '+' : ''}{change}{unit}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sales */}
              <div className="rounded-lg border border-rose-200 overflow-hidden">
                <div className="px-3 py-2 bg-rose-50 border-b border-rose-200 flex items-center justify-between">
                  <div className="text-xs uppercase font-bold text-rose-800">
                    🛒 {L('Sales', 'Verkäufe')}
                  </div>
                  <div className="text-[11px] text-rose-700 font-semibold">
                    {historySales.length} {L('items sold', 'verkauft')}
                  </div>
                </div>
                <div className="max-h-[400px] overflow-y-auto divide-y divide-rose-100 text-sm">
                  {historySales.length === 0 && (
                    <div className="px-3 py-6 text-center text-slate-400 italic text-xs">
                      {L('No sales recorded yet.', 'Noch keine Verkäufe erfasst.')}
                    </div>
                  )}
                  {historySales.map((s) => {
                    const date = s.created_at ? new Date(s.created_at) : null;
                    const isPool = !!historyItem?.stock_pool_id || items.some(i => i.stock_pool_id === historyItem?.id);
                    return (
                      <div key={s.id} className="px-3 py-2 flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-slate-700 font-semibold text-xs flex items-center gap-1.5">
                            {date ? date.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                            <span className="text-slate-400 ml-1">{date ? date.toLocaleTimeString(language === 'de' ? 'de-CH' : 'en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {isPool && s.item_name && s.item_name !== historyItem?.name && <span className="text-purple-500 font-semibold mr-1">{s.item_name}</span>}
                            {s.created_by_name || '—'}
                            <span className="ml-1 text-slate-300">· {s.payment_method}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-base font-extrabold text-rose-600 tabular-nums">
                            −{s.quantity}
                          </div>
                          <div className="text-[10px] text-slate-400 tabular-nums">
                            {s.total_price?.toFixed(2)} CHF
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!historyLoading && (
            <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-100">
              <div className="rounded-lg bg-emerald-50 p-3 text-center">
                <div className="text-[11px] uppercase font-bold text-emerald-700 mb-0.5">{L('Total in', 'Gesamt rein')}</div>
                <div className="text-lg font-extrabold text-emerald-700 tabular-nums">
                  +{historyMovements.reduce((a, m) => a + Math.max(0, Number(m.quantity_change || 0)), 0).toFixed(1)}{historyItem?.stock_unit === 'liters' ? 'L' : ''}
                </div>
              </div>
              <div className="rounded-lg bg-rose-50 p-3 text-center">
                <div className="text-[11px] uppercase font-bold text-rose-700 mb-0.5">{L('Units sold', 'Verkauft')}</div>
                <div className="text-lg font-extrabold text-rose-700 tabular-nums">
                  {historySales.reduce((a, s) => a + (s.quantity || 0), 0)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3 text-center">
                <div className="text-[11px] uppercase font-bold text-slate-600 mb-0.5">{L('Revenue', 'Umsatz')}</div>
                <div className="text-lg font-extrabold text-slate-800 tabular-nums">
                  {historySales.reduce((a, s) => a + (Number(s.total_price) || 0), 0).toFixed(2)} CHF
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setHistoryItem(null)} className="border-slate-200 text-slate-600">{L('Close', 'Schliessen')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Pool merge dialog (Bier-Fass) ====== */}
      <Dialog open={showMergeModal} onOpenChange={(o) => { if (!o) setShowMergeModal(false); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="merge-pool-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">{L('Merge into shared keg', 'Bier-Fass zusammenführen')}</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              {L(
                'Create one master stock (e.g. a single 30L keg) shared by multiple sellable items. A sale of any variant deducts the right amount of liters from the same counter.',
                'Ein gemeinsames Lager (z.B. ein 30L-Fass) für mehrere verkaufbare Artikel. Jeder Verkauf zieht die richtige Menge Liter vom gleichen Zähler ab.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-700">{L('Master keg name', 'Name des Master-Lagers')}</Label>
                <Input value={mergeForm.master_name} onChange={(e) => setMergeForm(f => ({ ...f, master_name: e.target.value }))}
                  data-testid="merge-master-name" placeholder="Bier-Fass (Lager)" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">{L('Category', 'Kategorie')}</Label>
                <Input value={mergeForm.master_category} onChange={(e) => setMergeForm(f => ({ ...f, master_category: e.target.value }))}
                  data-testid="merge-master-category" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">{L('Total quantity', 'Gesamtmenge')}</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" step="0.1" min="0" value={mergeForm.total_quantity}
                    onChange={(e) => setMergeForm(f => ({ ...f, total_quantity: e.target.value }))}
                    data-testid="merge-total-qty" placeholder="69.7" />
                  <Button type="button" onClick={() => setMergeForm(f => ({ ...f, total_quantity: String(mergeSelectedSum()) }))}
                    className="bg-slate-100 text-slate-700 hover:bg-slate-200 h-9 px-3 text-xs whitespace-nowrap" data-testid="merge-sumstocks-btn">
                    Σ {L('sum current', 'aktuelle Summe')}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">{L('Unit', 'Einheit')}</Label>
                <select value={mergeForm.stock_unit} onChange={(e) => setMergeForm(f => ({ ...f, stock_unit: e.target.value }))}
                  className="w-full h-9 px-2 rounded-md border border-slate-200 bg-white text-sm" data-testid="merge-unit">
                  <option value="liters">{L('Liters', 'Liter')}</option>
                  <option value="pcs">{L('Pieces', 'Stück')}</option>
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">
                {L('Members (pick items that share the keg)', 'Mitglieder (Artikel die sich das Fass teilen)')}
              </Label>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y">
                {items.filter(it => !it.stock_pool_id || mergeForm.selected[it.id]?.selected).map(it => {
                  const sel = mergeForm.selected[it.id] || { selected: false, deduction: '1' };
                  return (
                    <div key={it.id} className="flex items-center gap-2 p-2 hover:bg-slate-50">
                      <input type="checkbox" checked={!!sel.selected}
                        onChange={(e) => setMergeForm(f => ({ ...f, selected: { ...f.selected, [it.id]: { selected: e.target.checked, deduction: sel.deduction } } }))}
                        data-testid={`merge-select-${it.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{it.name}</div>
                        <div className="text-[11px] text-slate-400">
                          {it.category} · {it.stock_quantity != null && !it.stock_pool_id ? `${it.stock_quantity}${it.stock_unit === 'liters' ? 'L' : 'pc'} ` : ''}{it.price} CHF
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Input type="number" step="0.05" min="0.05" value={sel.deduction}
                          onChange={(e) => setMergeForm(f => ({ ...f, selected: { ...f.selected, [it.id]: { selected: sel.selected, deduction: e.target.value } } }))}
                          className="w-20 h-8 text-xs" disabled={!sel.selected} data-testid={`merge-deduction-${it.id}`} />
                        <span className="text-[10px] text-slate-400">{mergeForm.stock_unit === 'liters' ? 'L' : 'pc'}/{L('sale', 'Verkauf')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                {L(
                  'Tip: kl. Bier → 0.3 · gr. Bier → 0.5 · Herrgöttli → 0.2. Anything not listed has either no stock or already belongs to a pool.',
                  'Tipp: kl. Bier → 0.3 · gr. Bier → 0.5 · Herrgöttli → 0.2. Nicht aufgelistete Artikel haben keinen Lagerbestand oder sind bereits in einem Pool.'
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowMergeModal(false)}
              className="flex-1 border-slate-200 text-slate-600 rounded-lg" data-testid="merge-cancel-btn">
              {L('Cancel', 'Abbrechen')}
            </Button>
            <Button onClick={handleMergePool} disabled={mergeSubmitting}
              className="flex-1 bg-amber-500 text-white hover:bg-amber-600 font-bold rounded-lg" data-testid="merge-confirm-btn">
              {mergeSubmitting ? L('Working…', 'Arbeitet…') : L('Create shared keg', 'Fass zusammenführen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== Numpad popover for quick qty entry ====== */}
      <NumPad
        open={!!numpadFor}
        initial={numpadFor?.current || 0}
        max={numpadFor?.max ?? null}
        title={numpadFor ? `${numpadFor.name} × ?` : ''}
        subtitle={numpadFor?.max != null ? L(`Max ${numpadFor.max} in stock`, `Max. ${numpadFor.max} an Lager`) : null}
        onConfirm={(n) => { setCartLineQty(numpadFor.lineId, n); setNumpadFor(null); }}
        onClose={() => setNumpadFor(null)}
      />
    </div>
  );
};

export default AdminCashRegister;
